import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, Calendar, Clock, User, Check, X, MessageCircle, Mail,
  CalendarDays, CalendarRange, AlertCircle, GripVertical, Move, Plus, Menu, Edit, Ban, Trash2, UserPlus, Search, Sparkles, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth, api } from '../contexts/AuthContext';
import { useLanguage } from '../i18n';
import { useSiteSettings } from '../hooks/useSiteSettings';
import { toast } from 'sonner';
import {
  format, startOfWeek, endOfWeek, eachDayOfInterval,
  addDays, addWeeks, addMonths, startOfMonth, endOfMonth,
  isSameDay, isSameMonth, setHours, setMinutes
} from 'date-fns';
import { pt, enGB } from 'date-fns/locale';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

// ============================================================
// CONSTANTES
// ============================================================
const START_HOUR = 7;
const END_HOUR = 24;
const SLOT_HEIGHT = 60;
const EMPTY_ITEMS = [];
const EURO_FORMATTER = new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' });
const MOBILE_PRICE_FORMATTER = new Intl.NumberFormat('pt-PT', { maximumFractionDigits: 2 });
const euro = value => EURO_FORMATTER.format(Number(value) || 0);
const priceInCents = value => Math.round((Number(value) || 0) * 100);
const adjustedTotalCents = (baseCents, kind, percent) => Math.round(baseCents * (10000 + (kind === 'discount' ? -1 : kind === 'surcharge' ? 1 : 0) * Math.round(percent * 100)) / 10000);
const LISBON_TIME_FORMATTER = new Intl.DateTimeFormat('pt-PT', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Lisbon' });
const LISBON_DATE_FORMATTER = new Intl.DateTimeFormat('sv-SE', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'Europe/Lisbon' });
const lisbonDayKey = (value) => {
  const date = toUTCDate(value);
  return date && !isNaN(date.getTime()) ? LISBON_DATE_FORMATTER.format(date) : '';
};

// ============================================================
// CSS GLOBAL — Touch Drag & No Text Selection
// ============================================================
const touchDragStyles = `
  .schedule-card-draggable {
    -webkit-user-select: none;
    user-select: none;
    touch-action: none;
    -webkit-touch-callout: none;
  }
  .schedule-card-draggable:active {
    cursor: grabbing;
  }
  .touch-ghost {
    position: fixed;
    pointer-events: none;
    z-index: 9999;
    opacity: 0.85;
    transform: scale(0.95) rotate(2deg);
    box-shadow: 0 12px 24px rgba(0,0,0,0.25);
    border-radius: 8px;
    transition: none;
  }
  .schedule-slot-area {
    touch-action: auto;
    -webkit-user-select: none;
    user-select: none;
  }
  .schedule-slot-drop-target {
    position: relative;
    background: rgba(191, 160, 95, 0.2);
    border-color: #BFA05F;
  }
  .schedule-slot-drop-target::after {
    content: '';
    position: absolute;
    left: 4px;
    right: 4px;
    bottom: 0;
    height: 4px;
    background: #BFA05F;
    border-radius: 999px;
    pointer-events: none;
    z-index: 20;
  }
  /* Dialogs: one scroll container per modal; the action button stays in document flow. */
  .schedule-dialog-content {
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    gap: 0;
    width: calc(100vw - 24px);
    max-height: 92vh;
    max-height: min(92dvh, 920px);
    min-height: 0;
    overflow: hidden;
    padding: 20px;
  }
  .schedule-dialog-header {
    flex: 0 0 auto;
    padding-right: 28px;
    padding-bottom: 14px;
    border-bottom: 1px solid #E6E2DA;
    min-width: 0;
  }
  .schedule-dialog-scroll {
    flex: 1 1 auto;
    min-height: 0;
    min-width: 0;
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior-y: contain;
    -webkit-overflow-scrolling: touch;
    padding-top: 16px;
    padding-bottom: 12px;
  }
  .schedule-dialog-content input,
  .schedule-dialog-content textarea,
  .schedule-dialog-content button[data-state],
  .schedule-dialog-content [role="combobox"] {
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
  }
  .schedule-dialog-content input[type="datetime-local"] {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    display: block;
  }
  .schedule-dialog-content .schedule-modal-field-grid > * {
    min-width: 0;
  }
  .schedule-select-content {
    max-width: calc(100vw - 24px);
    max-height: 60vh;
    max-height: min(60dvh, 420px);
  }
  .schedule-modal-submit {
    position: static;
    padding-top: 12px;
    padding-bottom: 4px;
    background: transparent;
    border: 0;
    margin: 0;
  }
  .schedule-calendar-horizontal {
    width: 100%;
    min-width: 0;
    overflow-x: auto;
    overscroll-behavior-x: contain;
    -webkit-overflow-scrolling: touch;
  }
  /* Agenda — dois scrolls NATIVOS: horas na vertical e cabeçalho na horizontal.
     Os agendamentos só recebem a posição; não existe scroll manual por touchmove. */
  .schedule-week-layout {
    --schedule-rail-width: 60px;
    display: grid;
    grid-template-columns: var(--schedule-rail-width) minmax(0, 1fr);
    grid-template-rows: 64px minmax(0, 1fr);
    width: 100%;
    min-width: 0;
    height: clamp(350px, calc(100dvh - 270px), 920px);
    min-height: 0;
    overflow: hidden;
    isolation: isolate;
  }
  .schedule-week-corner {
    grid-column: 1;
    grid-row: 1;
    background: #F9F8F6;
    border-right: 1px solid #E6E2DA;
    border-bottom: 1px solid #E6E2DA;
    z-index: 2;
  }
  .schedule-week-header-viewport {
    grid-column: 2;
    grid-row: 1;
    min-width: 0;
    overflow: hidden;
    overscroll-behavior-x: contain;
    background: #F9F8F6;
    border-bottom: 1px solid #E6E2DA;
    z-index: 2;
  }
  .schedule-week-time-viewport {
    grid-column: 1;
    grid-row: 2;
    min-height: 0;
    overflow: hidden;
    border-right: 1px solid #E6E2DA;
    background: white;
    scrollbar-width: none;
  }
  .schedule-week-time-viewport::-webkit-scrollbar,
  .schedule-week-header-viewport::-webkit-scrollbar { display: none; }
  .schedule-week-board-viewport {
    grid-column: 2;
    grid-row: 2;
    min-width: 0;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior-y: contain;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
    scrollbar-color: #D1CBBE transparent;
  }
  .schedule-week-header-content,
  .schedule-week-board-content {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
  }
  .schedule-week-header-content[data-view="day"],
  .schedule-week-board-content[data-view="day"] {
    grid-template-columns: minmax(0, 1fr);
  }
  .schedule-week-header-cell { min-width: 0; height: 63px; }
  .schedule-week-board-content { min-height: max-content; }
  /* Dia/Semana em ecrãs maiores: o painel define a altura; a grelha ocupa-o sem crescer até à altura total dos horários. */
  @media (min-width: 640px) {
    .schedule-week-panel {
      height: clamp(560px, calc(100dvh - 130px), 920px);
      align-self: flex-start;
    }
    .schedule-week-layout.flex-1 {
      flex: 1 1 0%;
      height: auto;
      min-height: 0;
    }
  }
  @media (min-width: 1024px) {
    .schedule-week-panel {
      height: clamp(700px, calc(100dvh - 130px), 920px);
    }
    .schedule-week-sidebar {
      max-height: clamp(700px, calc(100dvh - 130px), 920px);
    }
  }
  @media (max-width: 639px) {
    .schedule-week-layout { height: clamp(350px, calc(100dvh - 270px), 850px); }
    .schedule-week-header-viewport {
      overflow-x: auto;
      overflow-y: hidden;
      touch-action: pan-x;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
    }
    .schedule-week-time-viewport {
      overflow-y: auto;
      overflow-x: hidden;
      touch-action: pan-y;
      overscroll-behavior-y: contain;
      -webkit-overflow-scrolling: touch;
    }
    .schedule-week-board-viewport {
      overflow: hidden;
      touch-action: none;
      scrollbar-width: none;
    }
    .schedule-week-header-content:not([data-view="day"]),
    .schedule-week-board-content:not([data-view="day"]) { width: 233.333333%; }
    .schedule-week-board-content .schedule-slot-area { touch-action: none; }
    .schedule-week-header-cell { touch-action: pan-x; }
    .schedule-week-time-viewport * { touch-action: pan-y; }
  }
  @media (max-width: 639px) {
    .schedule-dialog-content {
      padding: 18px 16px 0;
    }
    .schedule-dialog-content.schedule-dialog-fullscreen {
      position: fixed !important;
      inset: 0 !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      width: 100vw !important;
      max-width: 100vw !important;
      height: 100vh !important;
      height: 100dvh !important;
      max-height: 100vh !important;
      max-height: 100dvh !important;
      border-radius: 0 !important;
      transform: none !important;
      padding-top: max(18px, env(safe-area-inset-top, 0px));
      padding-bottom: max(16px, env(safe-area-inset-bottom, 0px));
    }
    .schedule-dialog-content.schedule-dialog-sheet {
      position: fixed !important;
      top: auto !important;
      bottom: 0 !important;
      left: 0 !important;
      right: 0 !important;
      width: 100vw !important;
      max-width: 100vw !important;
      max-height: 90vh !important;
      max-height: 90dvh !important;
      transform: none !important;
      border-radius: 20px 20px 0 0 !important;
      padding-bottom: max(16px, env(safe-area-inset-bottom, 0px));
      box-shadow: 0 -12px 36px rgba(0,0,0,0.12);
    }
    .schedule-dialog-content input,
    .schedule-dialog-content textarea,
    .schedule-dialog-content [role="combobox"] {
      font-size: 16px !important;
    }
    .schedule-dialog-content .schedule-dialog-scroll {
      scrollbar-width: thin;
    }
  }

  /* Semana inteligente: desenho independente, apenas no smartphone. */
  @media (max-width: 639px) {
    .schedule-week-layout[data-mobile-week-hidden="true"] { display: none !important; }
    .schedule-week-active .schedule-main-toolbar { gap: 10px; margin-bottom: 10px; }
    .schedule-week-active .schedule-week-panel { border-radius: 14px; }
    .schedule-mobile-focus-layout {
      display: flex;
      flex-direction: column;
      height: clamp(405px, calc(100dvh - 267px), 790px);
      min-width: 0;
      min-height: 0;
      background: #FFFFFF;
      overflow: hidden;
    }
    .schedule-mobile-focus-tools {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      padding: 6px 10px;
      border-bottom: 1px solid #E6E2DA;
      background: #FCFBF9;
      flex: 0 0 auto;
    }
    .schedule-mobile-focus-headers {
      display: grid;
      flex: 0 0 auto;
      width: 100%;
      min-height: 53px;
      border-bottom: 1px solid #E6E2DA;
      background: #FAF8F5;
    }
    .schedule-mobile-focus-day {
      min-width: 0;
      min-height: 53px;
      padding: 5px 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 2px;
      border-left: 1px solid #EAE5DB;
      color: #635E54;
      transition: background-color 160ms ease, color 160ms ease;
      -webkit-tap-highlight-color: transparent;
    }
    .schedule-mobile-focus-day.is-active { background: #EEE5D3; color: #614B28; }
    .schedule-mobile-focus-day.is-today .schedule-mobile-focus-date { box-shadow: inset 0 0 0 1px #BFA05F; }
    .schedule-mobile-focus-day.is-active .schedule-mobile-focus-date { background: #BFA05F; color: #FFFFFF; box-shadow: none; }
    .schedule-mobile-focus-dayname { font-size: 9px; line-height: 11px; font-weight: 700; text-transform: uppercase; white-space: nowrap; }
    .schedule-mobile-focus-date { width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; border-radius: 99px; font-size: 12px; line-height: 16px; font-weight: 700; }
    .schedule-mobile-focus-scroll {
      flex: 1 1 0%;
      min-height: 0;
      overflow-y: auto;
      overflow-x: hidden;
      overscroll-behavior-y: contain;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: thin;
      scrollbar-color: #D6CCBB transparent;
      touch-action: pan-y;
    }
    .schedule-mobile-focus-grid { display: grid; width: 100%; min-width: 0; align-items: stretch; }
    .schedule-mobile-focus-rail { position: relative; background: #FCFBF9; border-right: 1px solid #E6E2DA; }
    .schedule-mobile-focus-track {
      position: relative;
      min-width: 0;
      border-right: 1px solid #EAE5DB;
      touch-action: pan-y;
      -webkit-tap-highlight-color: transparent;
    }
    .schedule-mobile-focus-track.is-active { background-color: #FBF7EE; }
    .schedule-mobile-focus-track.is-today:not(.is-active) { background-color: #FDFBF7; }
    .schedule-mobile-focus-booking {
      position: absolute;
      left: 1px;
      right: 1px;
      padding: 2px 1px 1px 2px;
      border-left-width: 2px;
      border-radius: 4px;
      overflow: hidden;
      text-align: left;
      box-shadow: 0 1px 2px rgba(0,0,0,.10);
      touch-action: pan-y;
      -webkit-tap-highlight-color: transparent;
    }
    .schedule-mobile-focus-booking.is-focused { padding: 2px 4px 1px; border-left-width: 3px; }
    .schedule-mobile-focus-booking:focus-visible,
    .schedule-mobile-focus-day:focus-visible { outline: 2px solid #80632C; outline-offset: -2px; }
    .schedule-mobile-focus-name,
    .schedule-mobile-focus-hour,
    .schedule-mobile-focus-price,
    .schedule-mobile-focus-service {
      display: block;
      width: 100%;
      min-width: 0;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
    .schedule-mobile-focus-name { font-size: 9px; font-weight: 700; line-height: 11px; }
    .schedule-mobile-focus-hour { font-size: 8.5px; font-weight: 600; line-height: 11px; letter-spacing: -.2px; }
    .schedule-mobile-focus-price { font-size: 9px; font-weight: 800; line-height: 11px; letter-spacing: -.2px; }
    .schedule-mobile-focus-service { font-size: 10px; font-weight: 500; line-height: 12px; opacity: .96; }
    .schedule-mobile-focus-booking.is-focused .schedule-mobile-focus-name { font-size: 10.5px; line-height: 12px; }
    .schedule-mobile-focus-booking.is-focused .schedule-mobile-focus-hour { font-size: 9px; line-height: 10px; letter-spacing: normal; }
    .schedule-mobile-focus-booking.is-focused .schedule-mobile-focus-service { font-size: 9px; line-height: 10px; }
    .schedule-mobile-focus-booking.is-focused .schedule-mobile-focus-price { font-size: 9.5px; line-height: 10px; }
    .schedule-mobile-focus-buffer { position: absolute; left: 2px; right: 2px; background: rgba(174,168,156,.20); border-left: 2px dashed #C9C3B8; pointer-events: none; }
    .schedule-mobile-focus-block {
      position: absolute;
      left: 1px;
      right: 1px;
      background: #FEE2E2;
      border-left: 2px solid #B91C1C;
      border-radius: 4px;
      padding: 2px;
      overflow: hidden;
      color: #991B1B;
      font-size: 9px;
      font-weight: 700;
      line-height: 11px;
      text-align: left;
      overflow-wrap: anywhere;
      touch-action: pan-y;
    }
    .schedule-mobile-focus-block.is-focused { padding: 3px 4px; border-left-width: 3px; font-size: 11px; line-height: 13px; }
    .schedule-mobile-focus-block-reason { display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 5; overflow: hidden; }
    .schedule-mobile-focus-now { position: absolute; left: 0; right: 0; border-top: 1.5px solid #C44949; z-index: 12; pointer-events: none; }
    .schedule-mobile-focus-now::before { content: ''; position: absolute; top: -4px; left: 0; width: 7px; height: 7px; border-radius: 50%; background: #C44949; }
  }

  @media (max-width: 639px) and (max-height: 450px) {
    .schedule-dialog-content.schedule-dialog-sheet {
      max-height: 100vh !important;
      max-height: 100dvh !important;
    }
  }

`;

// ============================================================
// UTILITÁRIAS DE TIMEZONE
// ============================================================
const toUTCDate = (isoStringOrDate) => {
  if (!isoStringOrDate) return null;
  if (isoStringOrDate instanceof Date) return isoStringOrDate;
  const s = String(isoStringOrDate).trim();
  if (s.endsWith('Z') || s.includes('+') || /\d{2}-\d{2}:\d{2}$/.test(s)) return new Date(s);
  return new Date(s + 'Z');
};

const formatLisbon = (isoStringOrDate) => {
  const d = toUTCDate(isoStringOrDate);
  if (!d || isNaN(d.getTime())) return '--:--';
  return LISBON_TIME_FORMATTER.format(d);
};

const getLisbonHourMinute = (isoStringOrDate) => {
  const d = toUTCDate(isoStringOrDate);
  if (!d || isNaN(d.getTime())) return { hour: 0, minute: 0 };
  const s = LISBON_TIME_FORMATTER.format(d);
  const [h, m] = s.split(':').map(Number);
  return { hour: h, minute: m };
};

const isSameDayLisbon = (isoStringOrDate, dayDate) => {
  const d = toUTCDate(isoStringOrDate);
  if (!d || isNaN(d.getTime())) return false;
  return lisbonDayKey(d) === lisbonDayKey(dayDate);
};

const datetimeLocalToUTC = (datetimeLocalStr) => {
  if (!datetimeLocalStr) return null;
  const [datePart, timePart] = datetimeLocalStr.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);
  return new Date(year, month - 1, day, hours, minutes, 0, 0).toISOString();
};

// ============================================================
// TOUCH DRAG SYSTEM — Mobile drag support (CORRIGIDO)
// ============================================================
const useTouchDrag = ({ viewMode, onDragStart, onDropAtSlot, onDragEnd }) => {
  const touchState = useRef({ 
    active: false, 
    booking: null, 
    ghost: null, 
    startX: 0, 
    startY: 0, 
    moved: false,
    cardEl: null,
    longPressTimer: null,
    longPressTriggered: false,
    isScrolling: false,
    highlightedSlot: null,
    ghostWidth: 0
  });

  const LONG_PRESS_DELAY = 550;

  const createGhost = useCallback((sourceEl) => {
    const rect = sourceEl.getBoundingClientRect();
    const ghost = sourceEl.cloneNode(true);
    ghost.className = 'touch-ghost';
    ghost.style.width = `${rect.width}px`;
    ghost.style.height = `${Math.min(rect.height, 80)}px`;
    ghost.style.overflow = 'hidden';
    ghost.style.left = '0';
    ghost.style.top = '0';
    ghost.style.transform = `translate3d(${rect.left}px, ${rect.top}px, 0) scale(0.95) rotate(2deg)`;
    ghost.style.willChange = 'transform';
    document.body.appendChild(ghost);
    return ghost;
  }, []);

  const handleTouchStart = useCallback((e, booking, cardEl) => {
    if (booking.status === 'cancelled' || booking.status === 'completed') return;
    
    const touch = e.touches[0];
    
    const prevState = touchState.current;
    if (prevState.longPressTimer) clearTimeout(prevState.longPressTimer);
    
    touchState.current = { 
      active: true, 
      booking, 
      ghost: null, 
      startX: touch.clientX, 
      startY: touch.clientY, 
      moved: false, 
      cardEl,
      longPressTimer: null,
      longPressTriggered: false,
      isScrolling: false,
      highlightedSlot: null,
      ghostWidth: cardEl.getBoundingClientRect().width
    };

    const state = touchState.current;

    state.longPressTimer = setTimeout(() => {
      if (touchState.current.active && touchState.current.booking === booking) {
        touchState.current.longPressTriggered = true;
        touchState.current.ghost = createGhost(cardEl);
        cardEl.style.opacity = '0.3';
        onDragStart(booking);
        if (navigator.vibrate) navigator.vibrate(50);
      }
    }, LONG_PRESS_DELAY);

  }, [createGhost, onDragStart]);

  const handleTouchMove = useCallback((e) => {
    const state = touchState.current;
    if (!state.active) return;
    
    const touch = e.touches[0];
    const dx = touch.clientX - state.startX;
    const dy = touch.clientY - state.startY;
    const totalMovement = Math.abs(dx) + Math.abs(dy);

    if (!state.longPressTriggered) {
      if (totalMovement > 15) {
        state.isScrolling = true;
        if (state.longPressTimer) {
          clearTimeout(state.longPressTimer);
          state.longPressTimer = null;
        }
        state.active = false;
        return;
      }
      return;
    }

    if (!state.moved) {
      state.moved = true;
      e.preventDefault();
    }

    if (state.ghost) {
      state.ghost.style.transform = `translate3d(${touch.clientX - state.ghostWidth / 2}px, ${touch.clientY - 30}px, 0) scale(0.95) rotate(2deg)`;
    }

    if (state.ghost) state.ghost.style.display = 'none';
    const target = document.elementFromPoint(touch.clientX, touch.clientY);
    if (state.ghost) state.ghost.style.display = '';

    if (target) {
      const slot = target.closest('[data-slot-index]');
      if (slot) {
        const slotIndex = parseInt(slot.dataset.slotIndex);
        const dayStr = slot.dataset.slotDay;
        if (dayStr && !isNaN(slotIndex)) {
          if (state.highlightedSlot !== slot) {
            if (state.highlightedSlot) state.highlightedSlot.classList.remove('touch-drop-highlight', 'schedule-slot-drop-target');
            slot.classList.add('touch-drop-highlight', 'schedule-slot-drop-target');
            state.highlightedSlot = slot;
          }
        }
      }
    }

    e.preventDefault();
  }, []);

  const handleTouchEnd = useCallback((e) => {
    const state = touchState.current;
    if (!state.active) return;

    if (state.longPressTimer) {
      clearTimeout(state.longPressTimer);
      state.longPressTimer = null;
    }

    if (state.highlightedSlot) state.highlightedSlot.classList.remove('touch-drop-highlight', 'schedule-slot-drop-target');

    if (!state.longPressTriggered) {
      if (state.cardEl) state.cardEl.style.opacity = '1';
      touchState.current = { 
        active: false, 
        booking: null, 
        ghost: null, 
        startX: 0, 
        startY: 0, 
        moved: false, 
        cardEl: null,
        longPressTimer: null,
        longPressTriggered: false,
        isScrolling: false
      };
      return;
    }

    if (state.ghost) {
      const lastTouch = e.changedTouches[0];
      state.ghost.style.display = 'none';
      const target = document.elementFromPoint(lastTouch.clientX, lastTouch.clientY);
      state.ghost.remove();

      if (target) {
        const slot = target.closest('[data-slot-index]');
        if (slot) {
          const slotIndex = parseInt(slot.dataset.slotIndex);
          const dayStr = slot.dataset.slotDay;
          if (dayStr && !isNaN(slotIndex)) {
            onDropAtSlot(state.booking, new Date(dayStr), slotIndex);
          }
        }
      }
    }

    if (state.cardEl) state.cardEl.style.opacity = '1';
    onDragEnd();
    
    touchState.current = { 
      active: false, 
      booking: null, 
      ghost: null, 
      startX: 0, 
      startY: 0, 
      moved: false, 
      cardEl: null,
      longPressTimer: null,
      longPressTriggered: false,
      isScrolling: false
    };
  }, [onDropAtSlot, onDragEnd]);

  useEffect(() => {
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: false });
    return () => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchMove, handleTouchEnd]);

  return { handleTouchStart };
};

// ============================================================
// BookingForm — estado local, zero re-renders no pai ao digitar
// ============================================================
const BookingForm = memo(({ open, onClose, onSubmit, staff, services, clients, clientsLoading, clientsLoaded, initialStartTime, language, initialExisting = false, bookingSaving = false, sourceBooking = null }) => {
  const [isNewClient, setIsNewClient] = useState(true);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedServices, setSelectedServices] = useState([]);
  const [adjustmentType, setAdjustmentType] = useState('none');
  const [adjustmentPercent, setAdjustmentPercent] = useState('');
  // Apenas confirmações imediatas da criação. Os lembretes de no-show mantêm-se.
  const [sendConfirmationEmail, setSendConfirmationEmail] = useState(true);
  const [sendConfirmationWhatsapp, setSendConfirmationWhatsapp] = useState(true);
  const [clientSearch, setClientSearch] = useState('');
  const [serviceSearch, setServiceSearch] = useState('');
  const [historySuggestions, setHistorySuggestions] = useState(null);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [selectedSuggestionIds, setSelectedSuggestionIds] = useState([]);
  // Multidata: isolado das marcações normais. A equipa escolhe cada dia e hora livre.
  const [multiDateMode, setMultiDateMode] = useState(false);
  const [multiDateItems, setMultiDateItems] = useState([]);
  const [draftDate, setDraftDate] = useState('');
  const [pickerMonth, setPickerMonth] = useState('');
  const [dateSlots, setDateSlots] = useState([]);
  const [dateSlotsLoading, setDateSlotsLoading] = useState(false);
  const [dateSlotsError, setDateSlotsError] = useState('');
  const batchIdRef = useRef('');
  const batchFingerprintRef = useRef('');
  const lastAvailabilityConfigRef = useRef('');
  const [form, setForm] = useState({
    guest_name: '', guest_email: '', guest_phone: '',
    staff_id: '', start_time: initialStartTime || '', notes: ''
  });

  useEffect(() => {
    if (open) {
      const lourdyane = staff.find(s =>
        s.name.toLowerCase().includes('lourdyane') || s.name.toLowerCase().includes('neves')
      );
      const sourceUser = sourceBooking && clients.find(c => c.user_id === sourceBooking.user_id);
      const sourceGuest = sourceBooking?.guest_snapshot || {};
      const fromSource = Boolean(sourceBooking);
      setForm({
        guest_name: fromSource ? (sourceGuest.name || sourceBooking.user?.name || sourceUser?.name || sourceBooking.guest_name || '') : '',
        guest_email: fromSource ? (sourceGuest.email || sourceBooking.user?.email || sourceUser?.email || '') : '',
        guest_phone: fromSource ? (sourceGuest.phone || sourceBooking.user?.phone || sourceUser?.phone || '') : '',
        staff_id: fromSource ? (sourceBooking.staff_id || '') : (lourdyane?.staff_id || ''),
        start_time: fromSource ? '' : (initialStartTime || ''), notes: ''
      });
      setIsNewClient(fromSource ? !sourceUser : !initialExisting);
      setSelectedClientId(fromSource && sourceUser ? sourceUser.user_id : '');
      const sourceService = fromSource ? services.find(s => s.service_id === sourceBooking.service_id && s.is_active !== false) : null;
      setSelectedServices(sourceService ? [sourceService] : []);
      setAdjustmentType('none');
      setAdjustmentPercent('');
      setSendConfirmationEmail(true);
      setSendConfirmationWhatsapp(true);
      setClientSearch('');
      setServiceSearch('');
      setHistorySuggestions(null);
      setSelectedSuggestionIds([]);
      setMultiDateMode(fromSource);
      setMultiDateItems([]);
      const today = LISBON_DATE_FORMATTER.format(new Date());
      const initialDay = fromSource ? today : (initialStartTime?.slice(0, 10) || '');
      setDraftDate(initialDay);
      setPickerMonth((initialDay || today).slice(0, 7));
      setDateSlots([]);
      setDateSlotsError('');
      batchIdRef.current = `staffbatch_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
      batchFingerprintRef.current = '';
      lastAvailabilityConfigRef.current = '';
    }
  }, [open, initialStartTime, staff, sourceBooking, services]);

  // Cliente pré-preenchida também quando a lista carrega depois de abrir o modal.
  useEffect(() => {
    if (!open || !sourceBooking || !clientsLoaded) return;
    const registered = clients.find(c => c.user_id === sourceBooking.user_id);
    if (!registered) return;
    setIsNewClient(false);
    setSelectedClientId(registered.user_id);
    setForm(f => ({ ...f,
      guest_name: f.guest_name || registered.name || '',
      guest_email: f.guest_email || registered.email || '',
      guest_phone: f.guest_phone || registered.phone || '',
    }));
  }, [open, sourceBooking, clientsLoaded, clients]);

  // Buscar histórico da cliente selecionada (atalhos e sugestões)
  useEffect(() => {
    if (!open || isNewClient || !selectedClientId) {
      setHistorySuggestions(null);
      setSelectedSuggestionIds([]);
      return;
    }
    let active = true;
    setSuggestionsLoading(true);
    api.get(`/staff/client-history/${selectedClientId}`)
      .then((res) => {
        if (!active) return;
        setHistorySuggestions(res.data);
        // pré-selecionar os 2 serviços mais frequentes
        const top = (res.data?.services || []).slice(0, 2).map(s => s.service_id);
        setSelectedSuggestionIds(top);
      })
      .catch(() => { if (active) setHistorySuggestions(null); })
      .finally(() => { if (active) setSuggestionsLoading(false); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isNewClient, selectedClientId]);

  const applyHabitualTime = () => {
    const ht = historySuggestions?.habitual_time;
    if (!ht) return;
    let datePart;
    if (form.start_time && form.start_time.includes('T')) {
      datePart = form.start_time.split('T')[0];
    } else {
      const d = new Date();
      datePart = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }
    setForm(f => ({ ...f, start_time: `${datePart}T${ht}` }));
  };

  const toggleSuggestion = (sid) =>
    setSelectedSuggestionIds(prev => prev.includes(sid) ? prev.filter(x => x !== sid) : [...prev, sid]);

  const addSelectedSuggestions = () => {
    const toAdd = (historySuggestions?.services || []).filter(s => selectedSuggestionIds.includes(s.service_id));
    setSelectedServices(prev => {
      const existing = new Set(prev.map(p => p.service_id));
      const additions = toAdd
        .filter(s => !existing.has(s.service_id))
        .map(s => ({
          service_id: s.service_id, name_pt: s.name_pt, name_en: s.name_en,
          price: s.price, duration: s.duration, buffer_time: s.buffer_time,
        }));
      return [...prev, ...additions];
    });
  };

  const baseCents = selectedServices.reduce((sum, s) => sum + priceInCents(s.price), 0);
  const rawPercent = adjustmentPercent.trim().replace(',', '.');
  const maxPercent = adjustmentType === 'discount' ? 100 : 1000;
  const validPercent = adjustmentType === 'none' || (/^\d+(?:\.\d{1,2})?$/.test(rawPercent) && Number(rawPercent) <= maxPercent);
  const percentValue = adjustmentType === 'none' || !validPercent ? 0 : Number(rawPercent);
  const finalCents = adjustedTotalCents(baseCents, adjustmentType, percentValue);
  const adjustmentCents = finalCents - baseCents;
  const totalDuration = selectedServices.reduce((sum, s) => sum + (s.duration || 0) + (s.buffer_time || 15), 0);

  // Reutiliza a disponibilidade real da API, considerando TODOS os serviços e buffers.
  useEffect(() => {
    if (!open || !multiDateMode || !draftDate || !form.staff_id || !selectedServices.length) {
      setDateSlots([]);
      setDateSlotsLoading(false);
      setDateSlotsError('');
      return;
    }
    let alive = true;
    setDateSlots([]);
    setDateSlotsError('');
    setDateSlotsLoading(true);
    api.get('/bookings/available-slots', { params: {
      service_id: selectedServices[0].service_id,
      date: draftDate,
      staff_id: form.staff_id,
      total_duration: totalDuration,
    } }).then(({ data }) => {
      if (alive) setDateSlots(Array.isArray(data) ? data : []);
    }).catch(() => {
      if (alive) setDateSlotsError(language === 'pt' ? 'Não foi possível consultar os horários. Tenta novamente.' : 'Could not load available times. Please retry.');
    }).finally(() => { if (alive) setDateSlotsLoading(false); });
    return () => { alive = false; };
  }, [open, multiDateMode, draftDate, form.staff_id, selectedServices, totalDuration, language]);

  // Alterações aos serviços ou à profissional invalidam a seleção de horários anterior.
  const availabilityConfig = `${form.staff_id}|${selectedServices.map(s => s.service_id).join(',')}|${totalDuration}`;
  useEffect(() => {
    if (lastAvailabilityConfigRef.current && lastAvailabilityConfigRef.current !== availabilityConfig) {
      setMultiDateItems([]);
    }
    lastAvailabilityConfigRef.current = availabilityConfig;
  }, [availabilityConfig]);

  const submitBookingForm = () => {
    const fingerprint = JSON.stringify({ isNewClient, selectedClientId,
      form, selectedServices: selectedServices.map(s => s.service_id), adjustmentType,
      adjustmentPercent: percentValue, multiDateMode,
      sendConfirmationEmail, sendConfirmationWhatsapp,
      multiDateItems: multiDateItems.map(x => x.start_time) });
    if (batchFingerprintRef.current !== fingerprint) {
      batchIdRef.current = `staffbatch_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
      batchFingerprintRef.current = fingerprint;
    }
    onSubmit({ form, isNewClient, selectedClientId, selectedServices,
      adjustmentType, adjustmentPercent: percentValue, multiDateMode, multiDateItems,
      sendConfirmationEmail, sendConfirmationWhatsapp,
      batchId: batchIdRef.current });
  };

  const multiSlotOverlaps = (startISO) => {
    const startMs = new Date(startISO).getTime();
    const endMs = startMs + totalDuration * 60000;
    return multiDateItems.some(item => {
      const existingStart = new Date(item.start_time).getTime();
      return startMs < existingStart + totalDuration * 60000 && endMs > existingStart;
    });
  };
  const addMultiDateSlot = (slot) => {
    if (!slot?.start_time) return;
    if (multiSlotOverlaps(slot.start_time)) return;
    if (multiDateItems.length >= 12) {
      toast.error(language === 'pt' ? 'Máximo de 12 datas por operação.' : 'Maximum of 12 dates per operation.');
      return;
    }
    setMultiDateItems(prev => [...prev, {
      key: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
      date: lisbonDayKey(slot.start_time), start_time: slot.start_time,
    }].sort((a, b) => new Date(a.start_time) - new Date(b.start_time)));
  };


  // REGRA 1: Filtrar staff pelos serviços selecionados
  const eligibleStaff = staff.filter(s => s.is_active && (
    selectedServices.length === 0 ||
    selectedServices.every(svc => !svc.staff_ids || svc.staff_ids.length === 0 || svc.staff_ids.includes(s.staff_id))
  ));

  const handleClientChange = (value) => {
    setSelectedClientId(value);
    const client = clients.find(c => c.user_id === value);
    if (client) setForm(f => ({ ...f, guest_name: client.name, guest_email: client.email || '', guest_phone: client.phone || '' }));
  };

  const addService = (serviceId) => {
    const service = services.find(s => s.service_id === serviceId);
    if (service && !selectedServices.find(s => s.service_id === serviceId))
      setSelectedServices(prev => [...prev, service]);
  };

  const removeService = (index) => setSelectedServices(prev => prev.filter((_, i) => i !== index));

  const filteredClients = clients.filter(c => {
    const q = clientSearch.trim().toLowerCase();
    if (!q) return true;
    return (c.name || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q) || (c.phone || '').toLowerCase().includes(q);
  });
  const availableServices = services.filter(s => s.is_active && !selectedServices.find(sel => sel.service_id === s.service_id));
  const filteredServices = availableServices.filter(s => {
    const q = serviceSearch.trim().toLowerCase();
    if (!q) return true;
    return (s.name_pt || '').toLowerCase().includes(q) || (s.name_en || '').toLowerCase().includes(q);
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className={`schedule-dialog-content schedule-dialog-fullscreen booking-modal-content ${multiDateMode ? 'sm:max-w-[720px]' : 'sm:max-w-[600px]'}`} data-testid="booking-modal">
        <DialogHeader className="schedule-dialog-header">
          <DialogTitle className="text-[#1A1815] text-lg sm:text-xl leading-snug">{sourceBooking ? (language === 'pt' ? 'Marcar próximas visitas' : 'Book Next Visits') : multiDateMode ? (language === 'pt' ? 'Criar Várias Marcações' : 'Create Multiple Bookings') : (language === 'pt' ? 'Criar Marcação' : 'Create Booking')}</DialogTitle>
        </DialogHeader>
        <div className="schedule-dialog-scroll booking-modal-scroll space-y-4">
          <div className="flex gap-2 p-1 bg-[#F9F8F6] rounded-xl sm:rounded-full">
            <button onClick={() => setIsNewClient(false)}
              className={`flex-1 min-w-0 py-3 sm:py-2 px-2 sm:px-4 rounded-lg sm:rounded-full text-xs sm:text-sm font-medium transition-colors ${!isNewClient ? 'bg-[#BFA05F] text-white' : 'text-[#635E54] hover:text-[#1A1815]'}`}>
              {language === 'pt' ? 'Cliente Existente' : 'Existing Client'}
            </button>
            <button onClick={() => setIsNewClient(true)}
              className={`flex-1 min-w-0 py-3 sm:py-2 px-2 sm:px-4 rounded-lg sm:rounded-full text-xs sm:text-sm font-medium transition-colors ${isNewClient ? 'bg-[#BFA05F] text-white' : 'text-[#635E54] hover:text-[#1A1815]'}`}>
              {language === 'pt' ? 'Novo Cliente' : 'New Client'}
            </button>
          </div>

          {!isNewClient ? (
            <>
            <div>
              <label className="text-sm text-[#635E54]">{language === 'pt' ? 'Selecionar Cliente' : 'Select Client'} *</label>
              <Select value={selectedClientId} onValueChange={handleClientChange}>
                <SelectTrigger className="mt-1" data-testid="select-client-trigger"><SelectValue placeholder={language === 'pt' ? 'Escolha um cliente' : 'Choose a client'} /></SelectTrigger>
                <SelectContent className="schedule-select-content">
                  <div className="sticky top-0 bg-white z-10 p-2 border-b border-[#E6E2DA]">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A8A29A]" />
                      <Input
                        data-testid="client-search-input"
                        autoFocus
                        value={clientSearch}
                        onChange={e => setClientSearch(e.target.value)}
                        onKeyDown={e => e.stopPropagation()}
                        placeholder={language === 'pt' ? 'Pesquisar cliente...' : 'Search client...'}
                        className="pl-8 h-9"
                      />
                    </div>
                  </div>
                  {!clientsLoaded ? (
                    <div className="py-3 px-3 text-sm text-[#A8A29A] text-center" data-testid="client-search-empty">
                      {clientsLoading ? (language === 'pt' ? 'A carregar clientes...' : 'Loading clients...') : (language === 'pt' ? 'Não foi possível carregar clientes. Feche e reabra a janela.' : 'Could not load clients. Close and reopen.')}
                    </div>
                  ) : filteredClients.length === 0 ? (
                    <div className="py-3 px-3 text-sm text-[#A8A29A] text-center" data-testid="client-search-empty">
                      {language === 'pt' ? 'Nenhum cliente encontrado' : 'No client found'}
                    </div>
                  ) : filteredClients.map(client => (
                    <SelectItem key={client.user_id} value={client.user_id}>
                      {client.name} {client.email ? `(${client.email})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {suggestionsLoading && (
              <p className="text-xs text-[#A8A29A]" data-testid="suggestions-loading">
                {language === 'pt' ? 'A carregar sugestões do histórico...' : 'Loading history suggestions...'}
              </p>
            )}

            {historySuggestions?.has_history && (
              <div className="border border-[#BFA05F]/30 rounded-xl p-4 bg-[#FBF9F4]" data-testid="history-suggestions-panel">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-[#BFA05F]" />
                  <span className="text-sm font-semibold text-[#1A1815]">
                    {language === 'pt' ? 'Atalhos e Sugestões do Histórico' : 'History Shortcuts & Suggestions'}
                  </span>
                </div>

                {historySuggestions.habitual_time && (
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3 bg-white rounded-lg border border-[#E6E2DA] px-3 py-2">
                    <span className="text-sm text-[#635E54] flex items-center gap-1.5">
                      <Clock className="w-4 h-4 text-[#BFA05F]" />
                      {language === 'pt' ? 'Costuma agendar às' : 'Usually books at'}{' '}
                      <strong className="text-[#1A1815]">{historySuggestions.habitual_time}</strong>
                    </span>
                    <Button type="button" size="sm" variant="outline" data-testid="use-habitual-time-btn"
                      onClick={applyHabitualTime}
                      className="self-start sm:self-auto rounded-full border-[#BFA05F] text-[#BFA05F] hover:bg-[#BFA05F] hover:text-white min-h-9">
                      {language === 'pt' ? `Usar ${historySuggestions.habitual_time}` : `Use ${historySuggestions.habitual_time}`}
                    </Button>
                  </div>
                )}

                <div className="space-y-2">
                  {historySuggestions.services.map((s) => {
                    const checked = selectedSuggestionIds.includes(s.service_id);
                    return (
                      <button type="button" key={s.service_id}
                        data-testid={`suggestion-${s.service_id}`}
                        onClick={() => toggleSuggestion(s.service_id)}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg border text-left transition-colors ${checked ? 'border-[#BFA05F] bg-[#BFA05F]/5' : 'border-[#E6E2DA] bg-white hover:border-[#BFA05F]/50'}`}>
                        <span className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 border ${checked ? 'bg-[#BFA05F] border-[#BFA05F]' : 'border-[#C9C3B8] bg-white'}`}>
                          {checked && <Check className="w-3.5 h-3.5 text-white" />}
                        </span>
                        <span className="flex-1 text-sm text-[#1A1815]">
                          {language === 'pt' ? s.name_pt : s.name_en}
                          <span className="text-[#A8A29A] ml-1">({language === 'pt' ? 'Agendado' : 'Booked'} {s.count}x)</span>
                        </span>
                        <span className="text-sm font-medium text-[#635E54]">€{s.price}</span>
                      </button>
                    );
                  })}
                </div>

                <Button type="button" data-testid="add-suggestions-btn"
                  onClick={addSelectedSuggestions}
                  disabled={selectedSuggestionIds.length === 0}
                  className="w-full mt-3 bg-[#1A1815] text-white hover:bg-[#BFA05F] rounded-full h-9 disabled:opacity-50 disabled:cursor-not-allowed">
                  <Plus className="w-4 h-4 mr-1.5" />
                  {language === 'pt' ? `Adicionar Selecionados (${selectedSuggestionIds.length})` : `Add Selected (${selectedSuggestionIds.length})`}
                </Button>
              </div>
            )}
            </>
          ) : (
            <>
              <div>
                <label className="text-sm text-[#635E54]">{language === 'pt' ? 'Nome do Cliente' : 'Client Name'} *</label>
                <Input value={form.guest_name} onChange={e => setForm(f => ({ ...f, guest_name: e.target.value }))}
                  placeholder={language === 'pt' ? 'Nome completo' : 'Full name'} className="mt-1" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 schedule-modal-field-grid">
                <div>
                  <label className="text-sm text-[#635E54]">Email {language === 'pt' ? '(opcional)' : '(optional)'}</label>
                  <Input type="email" value={form.guest_email} onChange={e => setForm(f => ({ ...f, guest_email: e.target.value }))}
                    placeholder="email@exemplo.com" className="mt-1" />
                  <p className="text-xs text-[#A8A29A] mt-1">{language === 'pt' ? 'Para enviar notificações' : 'To send notifications'}</p>
                </div>
                <div>
                  <label className="text-sm text-[#635E54]">WhatsApp {language === 'pt' ? '(opcional)' : '(optional)'}</label>
                  <Input value={form.guest_phone} onChange={e => setForm(f => ({ ...f, guest_phone: e.target.value }))}
                    placeholder="+351 912 345 678" className="mt-1" />
                  <p className="text-xs text-[#A8A29A] mt-1">{language === 'pt' ? 'Para enviar notificações' : 'To send notifications'}</p>
                </div>
              </div>
            </>
          )}

          <div className="border border-[#E6E2DA] rounded-xl p-4 bg-[#F9F8F6]">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-[#1A1815]">{language === 'pt' ? 'Serviços' : 'Services'} *</label>
              <Badge variant="secondary" className="bg-[#BFA05F]/10 text-[#BFA05F]">
                {selectedServices.length} {language === 'pt' ? 'selecionado(s)' : 'selected'}
              </Badge>
            </div>
            {selectedServices.length > 0 && (
              <div className="space-y-2 mb-4">
                {selectedServices.map((svc, index) => (
                  <div key={`${svc.service_id}-${index}`}
                    className="flex items-center justify-between gap-2 min-w-0 bg-white p-3 rounded-lg border border-[#E6E2DA]">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-8 h-8 flex-shrink-0 rounded-full bg-[#BFA05F]/10 flex items-center justify-center text-[#BFA05F] font-bold text-sm">{index + 1}</div>
                      <div className="min-w-0">
                        <p className="font-medium text-[#1A1815] text-sm break-words">{language === 'pt' ? svc.name_pt : svc.name_en}</p>
                        <p className="text-xs text-[#635E54]">€{svc.price} • {svc.duration}min</p>
                      </div>
                    </div>
                    <button type="button" aria-label={language === 'pt' ? 'Remover serviço' : 'Remove service'} onClick={() => removeService(index)} className="shrink-0 p-2 hover:bg-red-50 rounded-full text-[#A8A29A] hover:text-red-500 transition-colors">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <div className="flex flex-wrap items-center justify-between gap-1 pt-2 border-t border-[#E6E2DA] text-sm">
                  <span className="text-[#635E54]">Total:</span>
                  <span className="font-semibold text-[#1A1815]">{euro(baseCents / 100)} • {totalDuration}min</span>
                </div>
              </div>
            )}
            <Select onValueChange={addService}>
              <SelectTrigger data-testid="select-service-trigger" className={`w-full bg-white ${selectedServices.length === 0 ? 'border-dashed border-2 border-[#BFA05F]/30 hover:border-[#BFA05F]' : ''} transition-colors`}>
                <div className="flex items-center gap-2 text-[#BFA05F]">
                  <Plus className="w-4 h-4" />
                  <span>{selectedServices.length === 0 ? (language === 'pt' ? 'Adicionar serviço' : 'Add service') : (language === 'pt' ? 'Adicionar outro serviço' : 'Add another service')}</span>
                </div>
              </SelectTrigger>
              <SelectContent className="schedule-select-content">
                <div className="sticky top-0 bg-white z-10 p-2 border-b border-[#E6E2DA]">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A8A29A]" />
                    <Input
                      data-testid="service-search-input"
                      autoFocus
                      value={serviceSearch}
                      onChange={e => setServiceSearch(e.target.value)}
                      onKeyDown={e => e.stopPropagation()}
                      placeholder={language === 'pt' ? 'Pesquisar serviço...' : 'Search service...'}
                      className="pl-8 h-9"
                    />
                  </div>
                </div>
                {filteredServices.length === 0 ? (
                  <div className="py-3 px-3 text-sm text-[#A8A29A] text-center" data-testid="service-search-empty">
                    {language === 'pt' ? 'Nenhum serviço encontrado' : 'No service found'}
                  </div>
                ) : filteredServices.map(service => (
                  <SelectItem key={service.service_id} value={service.service_id}>
                    <div className="flex items-start justify-between w-full min-w-0 gap-3">
                      <span className="min-w-0 whitespace-normal break-words text-left">{language === 'pt' ? service.name_pt : service.name_en}</span>
                      <span className="shrink-0 text-[#635E54] text-xs">€{service.price} • {service.duration}min</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="border border-[#E6E2DA] rounded-xl p-4 space-y-3 bg-white">
            <label className="text-sm font-medium text-[#1A1815]">{language === 'pt' ? 'Desconto ou acréscimo (%)' : 'Discount or surcharge (%)'}</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 schedule-modal-field-grid">
              <Select value={adjustmentType} onValueChange={v => { setAdjustmentType(v); setAdjustmentPercent(''); }}>
                <SelectTrigger data-testid="booking-adjustment-type"><SelectValue /></SelectTrigger>
                <SelectContent className="schedule-select-content">
                  <SelectItem value="none">{language === 'pt' ? 'Sem ajuste' : 'No adjustment'}</SelectItem>
                  <SelectItem value="discount">{language === 'pt' ? 'Desconto' : 'Discount'}</SelectItem>
                  <SelectItem value="surcharge">{language === 'pt' ? 'Acréscimo' : 'Surcharge'}</SelectItem>
                </SelectContent>
              </Select>
              <div className="relative">
                <Input value={adjustmentPercent} disabled={adjustmentType === 'none'}
                  onChange={e => setAdjustmentPercent(e.target.value)}
                  inputMode="decimal" data-testid="booking-adjustment-percent"
                  placeholder="0,00" className="pr-8" aria-label={language === 'pt' ? 'Percentagem de ajuste' : 'Adjustment percentage'} />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#635E54] text-sm pointer-events-none">%</span>
              </div>
            </div>
            {!validPercent && <p className="text-xs text-red-600">{language === 'pt' ? `Introduza uma percentagem válida entre 0 e ${maxPercent}, até 2 casas decimais.` : `Enter a valid percentage between 0 and ${maxPercent}, up to 2 decimal places.`}</p>}
            {selectedServices.length > 0 && (
              <div className="border-t border-[#E6E2DA] pt-3 space-y-1.5 text-sm">
                <div className="flex justify-between text-[#635E54]"><span>{language === 'pt' ? 'Subtotal' : 'Subtotal'}</span><span>{euro(baseCents / 100)}</span></div>
                {adjustmentType !== 'none' && validPercent && <div className={`flex justify-between ${adjustmentType === 'discount' ? 'text-green-700' : 'text-amber-700'}`}>
                  <span>{adjustmentType === 'discount' ? (language === 'pt' ? 'Desconto' : 'Discount') : (language === 'pt' ? 'Acréscimo' : 'Surcharge')} ({percentValue}%)</span>
                  <span>{adjustmentCents > 0 ? '+' : ''}{euro(adjustmentCents / 100)}</span>
                </div>}
                <div className="flex justify-between text-[#1A1815] font-bold text-base pt-1">
                  <span>{language === 'pt' ? 'Total da marcação' : 'Booking total'}</span>
                  <span data-testid="booking-adjusted-total">{validPercent ? euro(finalCents / 100) : '—'}</span>
                </div>
                <p className="text-xs text-[#A8A29A]">{language === 'pt' ? 'Ajuste aplicado aos serviços desta marcação, sem alterar os preços do catálogo.' : 'Adjustment applies to this booking only; catalogue prices stay unchanged.'}</p>
              </div>
            )}
          </div>

          <div>
            <label className="text-sm text-[#635E54]">{language === 'pt' ? 'Profissional' : 'Professional'}</label>
            <Select value={form.staff_id || 'any'} onValueChange={v => setForm(f => ({ ...f, staff_id: v === 'any' ? '' : v }))}>
              <SelectTrigger className="mt-1"><SelectValue placeholder={language === 'pt' ? 'Selecionar profissional' : 'Select professional'} /></SelectTrigger>
              <SelectContent className="schedule-select-content">
                {eligibleStaff.map(member => (
                  <SelectItem key={member.staff_id} value={member.staff_id}>{member.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <section className="space-y-3" data-testid="schedule-date-selection">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <label className="text-sm font-medium text-[#1A1815]">{language === 'pt' ? 'Data e hora' : 'Date and time'} *</label>
              {!sourceBooking && <button type="button" role="switch" aria-checked={multiDateMode}
                data-testid="staff-multidate-toggle"
                onClick={() => { setMultiDateMode(mode => !mode); setMultiDateItems([]); }}
                className={`inline-flex min-h-10 items-center gap-2 px-3 py-2 border rounded-full text-xs font-semibold ${multiDateMode ? 'bg-[#1A1815] text-white border-[#1A1815]' : 'border-[#BFA05F] text-[#80632C] bg-[#FBF9F4]'}`}>
                <CalendarRange className="w-4 h-4 shrink-0" />
                {language === 'pt' ? 'Várias datas' : 'Multiple dates'}
              </button>}
              {sourceBooking && <span className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-[#BFA05F]/10 text-[#80632C] text-xs font-semibold"><CalendarDays className="w-4 h-4" />{language === 'pt' ? 'Uma ou várias datas (até 12)' : 'One or multiple dates (up to 12)'}</span>}
            </div>
            {!multiDateMode ? (
              <>
                <Input type="datetime-local" value={form.start_time}
                  onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))} className="mt-1" />
                {selectedServices.length > 1 && (
                  <p className="text-xs text-[#BFA05F] mt-1">
                    {language === 'pt'
                      ? `Os ${selectedServices.length} serviços serão agendados em sequência a partir deste horário`
                      : `The ${selectedServices.length} services will be scheduled in sequence from this time`}
                  </p>
                )}
              </>
            ) : (
              <div className="rounded-xl bg-[#FCFBF9] border border-[#E6E2DA] p-3 sm:p-4 space-y-3" data-testid="staff-multidate-selector">
                <p className="text-xs text-[#635E54]">{sourceBooking
                  ? (language === 'pt' ? 'Escolhe um ou vários dias no calendário e adiciona as horas livres (até 12 datas). As novas marcações não alteram a original.' : 'Choose one or more days on the calendar and add available times (up to 12 dates). Existing bookings stay unchanged.')
                  : (language === 'pt' ? 'Escolhe uma data e toca na hora disponível. Depois, usa + para escolher outra data.' : 'Choose a date and tap an available time. Then use + to pick another date.')}</p>
                {!form.staff_id && <p className="text-sm text-amber-700">{language === 'pt' ? 'Escolhe primeiro a profissional para consultar horários.' : 'Choose a professional first.'}</p>}
                {!selectedServices.length && <p className="text-sm text-amber-700">{language === 'pt' ? 'Escolhe primeiro os serviços.' : 'Choose services first.'}</p>}
                {sourceBooking && (() => {
                  const currentMonth = pickerMonth || LISBON_DATE_FORMATTER.format(new Date()).slice(0, 7);
                  const [year, month] = currentMonth.split('-').map(Number);
                  const firstWeekday = (new Date(year, month - 1, 1).getDay() + 6) % 7;
                  const daysInMonth = new Date(year, month, 0).getDate();
                  const today = LISBON_DATE_FORMATTER.format(new Date());
                  const monthLabel = new Date(year, month - 1, 1).toLocaleDateString(language === 'pt' ? 'pt-PT' : 'en-GB', { month: 'long', year: 'numeric' });
                  return <div className="max-w-sm rounded-xl border border-[#E6E2DA] bg-white p-3" data-testid="next-booking-calendar">
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <button type="button" onClick={() => { setPickerMonth(format(new Date(year, month - 2, 1), 'yyyy-MM')); setDraftDate(''); setDateSlots([]); }}
                        disabled={currentMonth <= today.slice(0, 7)} aria-label={language === 'pt' ? 'Mês anterior' : 'Previous month'} className="min-h-11 min-w-11 rounded-lg border flex items-center justify-center disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
                      <span className="font-semibold text-sm text-[#1A1815] capitalize">{monthLabel}</span>
                      <button type="button" onClick={() => { setPickerMonth(format(new Date(year, month, 1), 'yyyy-MM')); setDraftDate(''); setDateSlots([]); }}
                        aria-label={language === 'pt' ? 'Mês seguinte' : 'Next month'} className="min-h-11 min-w-11 rounded-lg border flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
                    </div>
                    <div className="grid grid-cols-7 gap-1 text-center text-[11px] text-[#635E54] mb-1">
                      {(language === 'pt' ? ['S','T','Q','Q','S','S','D'] : ['M','T','W','T','F','S','S']).map((d, i) => <span key={i}>{d}</span>)}
                    </div>
                    <div className="grid grid-cols-7 gap-1">
                      {Array.from({ length: firstWeekday }, (_, i) => <span key={`pad-${i}`} />)}
                      {Array.from({ length: daysInMonth }, (_, i) => {
                        const date = `${currentMonth}-${String(i + 1).padStart(2, '0')}`;
                        const selected = draftDate === date;
                        const queued = multiDateItems.some(item => item.date === date);
                        return <button type="button" key={date} disabled={date < today} onClick={() => setDraftDate(date)}
                          aria-label={`${language === 'pt' ? 'Ver horários' : 'See times'} ${date}`}
                          aria-pressed={selected}
                          className={`relative min-h-10 rounded-lg text-sm font-semibold disabled:opacity-30 disabled:cursor-not-allowed ${selected ? 'bg-[#BFA05F] text-white' : queued ? 'bg-[#BFA05F]/15 text-[#80632C]' : 'bg-[#F9F8F6] text-[#1A1815] hover:bg-[#EEE7D9]'}`}>{i + 1}{queued && <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-current" />}</button>;
                      })}
                    </div>
                    <p className="text-[11px] text-[#817A70] mt-2">{language === 'pt' ? 'Dias anteriores desativados. Ao escolher um dia, são consultadas as vagas reais.' : 'Past dates are disabled. Selecting a day checks real availability.'}</p>
                  </div>;
                })()}
                <div className="grid grid-cols-1 sm:grid-cols-[minmax(135px,0.85fr)_minmax(0,1.5fr)] gap-3 items-start">
                  <div className="space-y-1.5 min-w-0">
                    <label htmlFor="staff-multidate-date" className="text-xs font-semibold text-[#635E54]">{language === 'pt' ? 'Data' : 'Date'}</label>
                    <input type="date" id="staff-multidate-date" data-testid="staff-multidate-date"
                      value={draftDate} min={new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Lisbon' })}
                      onChange={e => { setDraftDate(e.target.value); if (e.target.value) setPickerMonth(e.target.value.slice(0, 7)); }}
                      className="w-full min-w-0 min-h-12 rounded-lg border border-[#D8D3C9] bg-white px-3 text-base text-[#1A1815] focus:outline-none focus:ring-2 focus:ring-[#BFA05F]" />
                  </div>
                  <div className="space-y-2 min-w-0">
                    <p className="text-xs font-semibold text-[#635E54]">{language === 'pt' ? 'Horas disponíveis' : 'Available times'}</p>
                    {dateSlotsLoading ? <p className="text-sm text-[#635E54] py-3">{language === 'pt' ? 'A consultar horários…' : 'Loading times…'}</p>
                      : dateSlotsError ? <p role="alert" className="text-sm text-red-600 py-3">{dateSlotsError}</p>
                      : !draftDate || !form.staff_id || !selectedServices.length
                        ? <p className="text-sm text-[#8B857A] py-3">{language === 'pt' ? 'Seleciona a data, serviços e profissional.' : 'Select date, services and professional.'}</p>
                        : dateSlots.length === 0
                          ? <p className="text-sm font-medium text-red-700 rounded-lg bg-red-50 px-3 py-3" data-testid="staff-multidate-unavailable">{language === 'pt' ? 'Indisponível — sem horários livres neste dia.' : 'Unavailable — no free times on this day.'}</p>
                          : <div className="grid grid-cols-3 sm:grid-cols-3 gap-2 max-h-44 overflow-y-auto pr-1" data-testid="staff-multidate-times">
                            {dateSlots.map(slot => {
                              const alreadySelected = multiSlotOverlaps(slot.start_time);
                              return <button type="button" key={slot.start_time} disabled={alreadySelected}
                                data-testid="staff-multidate-time"
                                onClick={() => addMultiDateSlot(slot)}
                                aria-label={`${language === 'pt' ? 'Adicionar' : 'Add'} ${formatLisbon(slot.start_time)}`}
                                className={`min-h-11 rounded-lg border text-sm font-medium ${alreadySelected ? 'border-[#BFA05F] bg-[#BFA05F]/15 text-[#80632C] cursor-not-allowed' : 'bg-white border-[#E6E2DA] text-[#1A1815] hover:border-[#BFA05F] hover:bg-[#FBF9F4]'}`}>
                                {alreadySelected ? '✓ ' : ''}{formatLisbon(slot.start_time)}
                              </button>;
                            })}
                          </div>}
                  </div>
                </div>
                {multiDateItems.length > 0 && <div className="space-y-2 pt-2 border-t border-[#E6E2DA]">
                  <p className="font-semibold text-sm text-[#1A1815]">{language === 'pt' ? `Datas selecionadas (${multiDateItems.length})` : `Selected dates (${multiDateItems.length})`}</p>
                  <div className="space-y-1.5">
                    {multiDateItems.map((item, idx) => <div key={item.key} className="flex gap-2 items-center justify-between bg-white rounded-lg px-3 py-2 border border-[#E6E2DA]">
                      <div className="min-w-0 text-sm text-[#1A1815]">
                        <span className="font-semibold mr-2">{idx + 1}.</span>
                        <span>{new Date(item.start_time).toLocaleDateString(language === 'pt' ? 'pt-PT' : 'en-GB', { timeZone: 'Europe/Lisbon', weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                        <span className="font-bold text-[#80632C] ml-2">{formatLisbon(item.start_time)}</span>
                      </div>
                      <button type="button" title={language === 'pt' ? 'Remover data' : 'Remove date'} aria-label={language === 'pt' ? 'Remover data' : 'Remove date'}
                        onClick={() => setMultiDateItems(prev => prev.filter(x => x.key !== item.key))}
                        className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-red-600 hover:bg-red-50"><X className="h-4 w-4" /></button>
                    </div>)}
                  </div>
                  <button type="button" data-testid="staff-multidate-add-another"
                    onClick={() => { setDraftDate(''); setDateSlots([]); document.getElementById('staff-multidate-date')?.focus(); }}
                    className="inline-flex items-center justify-center gap-2 min-h-11 px-4 rounded-full border border-[#BFA05F] bg-white text-[#80632C] font-semibold text-sm hover:bg-[#FBF9F4]">
                    <Plus className="h-4 w-4" />{language === 'pt' ? 'Adicionar outra data' : 'Add another date'}
                  </button>
                  <div className="flex justify-between text-sm font-semibold text-[#1A1815] pt-2 border-t border-[#E6E2DA]">
                    <span>{language === 'pt' ? 'Total de todas as datas' : 'Total for all dates'}</span>
                    <span>{euro((validPercent ? finalCents : baseCents) * multiDateItems.length / 100)}</span>
                  </div>
                </div>}
              </div>
            )}
          </section>

          <div>
            <label className="text-sm text-[#635E54]">{language === 'pt' ? 'Notas (opcional)' : 'Notes (optional)'}</label>
            <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder={language === 'pt' ? 'Observações sobre o cliente ou marcação' : 'Notes about client or booking'}
              className="mt-1" rows={2} />
          </div>

          <section className="rounded-xl border border-[#E6E2DA] bg-[#FBFAF7] p-4 space-y-3" data-testid="staff-booking-comms">
            <div>
              <p className="text-sm font-semibold text-[#1A1815]">{language === 'pt' ? 'Enviar confirmação à cliente' : 'Send confirmation to client'}</p>
              <p className="text-xs text-[#635E54] mt-1">{language === 'pt' ? 'Escolhe que mensagens enviar agora ao criar a marcação.' : 'Choose which messages to send now when creating the booking.'}</p>
            </div>
            <label className="flex items-center gap-3 rounded-lg bg-white border border-[#E6E2DA] px-3 py-3 cursor-pointer min-h-12">
              <input type="checkbox" data-testid="staff-confirmation-whatsapp" checked={sendConfirmationWhatsapp}
                onChange={e => setSendConfirmationWhatsapp(e.target.checked)}
                className="h-5 w-5 accent-[#BFA05F] shrink-0" />
              <MessageCircle className="h-4 w-4 text-[#BFA05F] shrink-0" />
              <span className="text-sm font-medium text-[#1A1815]">{language === 'pt' ? 'Enviar confirmação por WhatsApp' : 'Send WhatsApp confirmation'}</span>
            </label>
            <label className="flex items-center gap-3 rounded-lg bg-white border border-[#E6E2DA] px-3 py-3 cursor-pointer min-h-12">
              <input type="checkbox" data-testid="staff-confirmation-email" checked={sendConfirmationEmail}
                onChange={e => setSendConfirmationEmail(e.target.checked)}
                className="h-5 w-5 accent-[#BFA05F] shrink-0" />
              <Mail className="h-4 w-4 text-[#BFA05F] shrink-0" />
              <span className="text-sm font-medium text-[#1A1815]">{language === 'pt' ? 'Enviar confirmação por email' : 'Send email confirmation'}</span>
            </label>
            <p className="text-xs text-[#80632C] leading-relaxed flex items-start gap-2">
              <Clock className="h-4 w-4 shrink-0 mt-0.5" />
              {language === 'pt' ? 'Os lembretes automáticos antes da marcação (anti no-show) continuam ativos, mesmo que desmarques ambas as opções.' : 'Automatic pre-appointment reminders stay enabled even if both options are unchecked.'}
            </p>
          </section>

          <div className="schedule-modal-submit booking-modal-submit">
            <Button onClick={submitBookingForm}
              disabled={bookingSaving || selectedServices.length === 0 || !validPercent || (!isNewClient && !clientsLoaded) || (sourceBooking && !clientsLoaded) || (multiDateMode && (!form.staff_id || multiDateItems.length === 0))}
              className="w-full bg-[#BFA05F] text-white hover:bg-[#9E8246] rounded-full disabled:opacity-50 disabled:cursor-not-allowed h-12 text-base">
              <Calendar className="w-4 h-4 mr-2" />
              {sourceBooking
                ? (language === 'pt' ? (multiDateItems.length === 1 ? 'Confirmar próxima marcação' : `Agendar ${multiDateItems.length} próximas datas`) : (multiDateItems.length === 1 ? 'Confirm next booking' : `Book ${multiDateItems.length} next dates`))
                : multiDateMode
                ? (language === 'pt' ? `Agendar ${multiDateItems.length} data(s)` : `Book ${multiDateItems.length} date(s)`)
                : selectedServices.length > 1
                  ? (language === 'pt' ? `Criar ${selectedServices.length} Agendamentos` : `Create ${selectedServices.length} Bookings`)
                  : (language === 'pt' ? 'Criar Agendamento' : 'Create Booking')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
});

// ============================================================
// EditBookingForm — estado local isolado
// ============================================================
const EditBookingForm = memo(({ booking, services, staff, clients, language, onSave, onBack }) => {
  const toLocalInput = (isoStr) => {
    const d = toUTCDate(isoStr);
    if (!d) return '';
    return d.toLocaleString('sv-SE', { timeZone: 'Europe/Lisbon' }).slice(0, 16).replace(' ', 'T');
  };

  // Resolve o nome/email/telefone do cliente a partir de todas as fontes possíveis:
  // 1. guest_snapshot (bookings criados manualmente pelo staff)
  // 2. booking.user (objeto populado pelo backend)
  // 3. lista de clients registados
  const resolveClientData = (bk) => {
    if (!bk) return { name: '', email: '', phone: '' };
    const snap = bk.guest_snapshot || {};
    const userObj = bk.user || {};
    const registered = clients.find(c => c.user_id === bk.user_id);
    return {
      name:  snap.name  || userObj.name  || registered?.name  || '',
      email: snap.email || userObj.email || registered?.email || '',
      phone: snap.phone || userObj.phone || registered?.phone || '',
    };
  };

  const buildForm = (bk) => {
    const clientData = resolveClientData(bk);
    // Se o user_id existe na lista de clientes registados, usa-o; senão trata como guest
    const registeredMatch = clients.find(c => c.user_id === bk?.user_id);
    return {
      service_id: bk?.service_id || '',
      staff_id:   bk?.staff_id   || '',
      // user_id só preenchido se for cliente registado (não guest/temporário)
      user_id:    registeredMatch ? bk.user_id : '',
      guest_name:  clientData.name,
      guest_email: clientData.email,
      guest_phone: clientData.phone,
      start_time: toLocalInput(bk?.start_time),
      adjustment_type: ['discount', 'surcharge'].includes(bk?.adjustment_type) ? bk.adjustment_type : 'none',
      adjustment_percent: String(bk?.adjustment_percent ?? 0).replace('.', ','),
    };
  };

  const [form, setForm] = useState(() => buildForm(booking));
  const [adjustmentChanged, setAdjustmentChanged] = useState(false);

  useEffect(() => {
    if (booking) {
      setForm(buildForm(booking));
      setAdjustmentChanged(false);
    }
  }, [booking]);

  const selectedService = services.find(s => s.service_id === form.service_id);
  const originalService = booking?.service_id === form.service_id;
  const originalBase = originalService && booking?.base_price != null ? booking.base_price : selectedService?.price ?? booking?.service?.price ?? 0;
  const baseCents = priceInCents(originalBase);
  const rawPercent = String(form.adjustment_percent ?? '').trim().replace(',', '.');
  const maxPercent = form.adjustment_type === 'discount' ? 100 : 1000;
  const validPercent = form.adjustment_type === 'none' || (/^\d+(?:\.\d{1,2})?$/.test(rawPercent) && Number(rawPercent) <= maxPercent);
  const percentValue = form.adjustment_type === 'none' || !validPercent ? 0 : Number(rawPercent);
  const finalCents = adjustedTotalCents(baseCents, form.adjustment_type, percentValue);
  const adjustmentCents = finalCents - baseCents;
  const paymentConfirmed = booking?.payment_status === 'paid';

  return (
    <div className="space-y-4 min-w-0">
      {/* Profissional */}
      <div>
        <label className="text-sm text-[#635E54]">{language === 'pt' ? 'Profissional' : 'Staff'} *</label>
        <Select value={form.staff_id} onValueChange={v => setForm(f => ({ ...f, staff_id: v }))}>
          <SelectTrigger className="mt-1"><SelectValue placeholder={language === 'pt' ? 'Selecionar profissional' : 'Select staff'} /></SelectTrigger>
          <SelectContent className="schedule-select-content">
            {staff.filter(s => s.is_active).map(s => <SelectItem key={s.staff_id} value={s.staff_id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Cliente — select para trocar, campos editáveis sempre visíveis */}
      <div>
        <label className="text-sm text-[#635E54]">{language === 'pt' ? 'Cliente' : 'Client'}</label>
        <Select
          value={form.user_id || 'guest'}
          onValueChange={v => {
            if (v === 'guest') {
              // Ao escolher "convidado", limpa só o user_id mas mantém os campos
              setForm(f => ({ ...f, user_id: '' }));
            } else {
              const c = clients.find(c => c.user_id === v);
              setForm(f => ({
                ...f,
                user_id:     v,
                guest_name:  c?.name  || '',
                guest_email: c?.email || '',
                guest_phone: c?.phone || '',
              }));
            }
          }}
        >
          <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
          <SelectContent className="schedule-select-content">
            <SelectItem value="guest">{language === 'pt' ? '— Cliente sem conta (convidado) —' : '— Guest client —'}</SelectItem>
            {clients.map(c => <SelectItem key={c.user_id} value={c.user_id}>{c.name} {c.email ? `(${c.email})` : ''}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Dados do cliente — SEMPRE visíveis, pré-preenchidos, editáveis */}
      <div className="space-y-3 p-3 bg-[#F9F8F6] rounded-lg border border-[#E6E2DA]">
        <p className="text-[10px] text-[#A8A29A] uppercase font-medium">
          {language === 'pt' ? 'Dados do cliente' : 'Client details'}
        </p>
        <div>
          <label className="text-xs text-[#635E54]">{language === 'pt' ? 'Nome' : 'Name'}</label>
          <Input
            value={form.guest_name}
            onChange={e => setForm(f => ({ ...f, guest_name: e.target.value }))}
            className="mt-1 min-h-10 text-sm"
            placeholder={language === 'pt' ? 'Nome do cliente' : 'Client name'}
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-[#635E54]">Email</label>
            <Input
              type="email"
              value={form.guest_email}
              onChange={e => setForm(f => ({ ...f, guest_email: e.target.value }))}
              className="mt-1 min-h-10 text-sm"
              placeholder="email@exemplo.com"
            />
          </div>
          <div>
            <label className="text-xs text-[#635E54]">{language === 'pt' ? 'Telefone' : 'Phone'}</label>
            <Input
              value={form.guest_phone}
              onChange={e => setForm(f => ({ ...f, guest_phone: e.target.value }))}
              className="mt-1 min-h-10 text-sm"
              placeholder="+351 912 345 678"
            />
          </div>
        </div>
      </div>

      {/* Serviço */}
      <div>
        <label className="text-sm text-[#635E54]">{language === 'pt' ? 'Serviço' : 'Service'} *</label>
        <Select value={form.service_id} onValueChange={v => setForm(f => ({ ...f, service_id: v }))}>
          <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
          <SelectContent className="schedule-select-content">
            {services.filter(s => s.is_active).map(s => (
              <SelectItem key={s.service_id} value={s.service_id}>
                {language === 'pt' ? s.name_pt : s.name_en} - €{s.price}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Desconto / acréscimo — mesma regra percentual da criação de marcações */}
      <div className="border border-[#E6E2DA] rounded-xl p-4 space-y-3 bg-white">
        <label className="text-sm font-medium text-[#1A1815]">{language === 'pt' ? 'Desconto ou acréscimo (%)' : 'Discount or surcharge (%)'}</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 schedule-modal-field-grid">
          <Select value={form.adjustment_type} disabled={paymentConfirmed} onValueChange={v => {
            setForm(f => ({ ...f, adjustment_type: v, adjustment_percent: '' }));
            setAdjustmentChanged(true);
          }}>
            <SelectTrigger data-testid="edit-booking-adjustment-type"><SelectValue /></SelectTrigger>
            <SelectContent className="schedule-select-content">
              <SelectItem value="none">{language === 'pt' ? 'Sem ajuste' : 'No adjustment'}</SelectItem>
              <SelectItem value="discount">{language === 'pt' ? 'Desconto' : 'Discount'}</SelectItem>
              <SelectItem value="surcharge">{language === 'pt' ? 'Acréscimo' : 'Surcharge'}</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative">
            <Input value={form.adjustment_percent} disabled={form.adjustment_type === 'none' || paymentConfirmed}
              onChange={e => {
                setForm(f => ({ ...f, adjustment_percent: e.target.value }));
                setAdjustmentChanged(true);
              }}
              inputMode="decimal" data-testid="edit-booking-adjustment-percent"
              placeholder="0,00" className="pr-8" aria-label={language === 'pt' ? 'Percentagem de ajuste' : 'Adjustment percentage'} />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#635E54] text-sm pointer-events-none">%</span>
          </div>
        </div>
        {!validPercent && <p className="text-xs text-red-600">{language === 'pt' ? `Introduza uma percentagem válida entre 0 e ${maxPercent}, até 2 casas decimais.` : `Enter a valid percentage between 0 and ${maxPercent}, up to 2 decimal places.`}</p>}
        {paymentConfirmed && <p className="text-xs text-amber-700">{language === 'pt' ? 'O pagamento desta marcação já foi confirmado. Para não alterar valores já pagos, o ajuste está bloqueado.' : 'Payment is already confirmed. Price adjustment is locked to protect the paid amount.'}</p>}
        {selectedService && (
          <div className="border-t border-[#E6E2DA] pt-3 space-y-1.5 text-sm">
            <div className="flex justify-between text-[#635E54]"><span>{language === 'pt' ? 'Preço base' : 'Base price'}</span><span>{euro(baseCents / 100)}</span></div>
            {form.adjustment_type !== 'none' && validPercent && <div className={`flex justify-between ${form.adjustment_type === 'discount' ? 'text-green-700' : 'text-amber-700'}`}>
              <span>{form.adjustment_type === 'discount' ? (language === 'pt' ? 'Desconto' : 'Discount') : (language === 'pt' ? 'Acréscimo' : 'Surcharge')} ({percentValue}%)</span>
              <span>{adjustmentCents > 0 ? '+' : ''}{euro(adjustmentCents / 100)}</span>
            </div>}
            <div className="flex justify-between text-[#1A1815] font-bold text-base pt-1">
              <span>{language === 'pt' ? 'Total da marcação' : 'Booking total'}</span>
              <span data-testid="edit-booking-adjusted-total">{validPercent ? euro(!adjustmentChanged && originalService && booking?.price != null ? booking.price : finalCents / 100) : '—'}</span>
            </div>
            <p className="text-xs text-[#A8A29A]">{language === 'pt' ? 'O ajuste aplica-se apenas a esta marcação, sem alterar os preços do catálogo.' : 'The adjustment applies only to this booking; catalogue prices stay unchanged.'}</p>
          </div>
        )}
      </div>

      {/* Data e Hora */}
      <div>
        <label className="text-sm text-[#635E54]">{language === 'pt' ? 'Data e Hora' : 'Date'} *</label>
        <Input
          type="datetime-local"
          value={form.start_time}
          onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))}
          className="mt-1"
        />
      </div>

      <div className="grid grid-cols-2 gap-2 pt-2 schedule-modal-field-grid">
        <Button onClick={onBack} variant="outline" className="flex-1 rounded-full">
          {language === 'pt' ? 'Voltar' : 'Back'}
        </Button>
        <Button onClick={() => onSave({ ...form, adjustment_changed: adjustmentChanged, adjustment_percent: percentValue })}
          disabled={!validPercent} className="flex-1 bg-[#BFA05F] text-white rounded-full disabled:opacity-50">
          {language === 'pt' ? 'Guardar' : 'Save'}
        </Button>
      </div>
    </div>
  );
});

// ============================================================
// BlockForm — estado local isolado
// ============================================================
const BlockForm = memo(({ open, onClose, editingBlock, staff, initialValues, language, onSave, onDelete }) => {
  const toLocalInput = (isoStr) => {
    const d = toUTCDate(isoStr);
    if (!d) return '';
    return d.toLocaleString('sv-SE', { timeZone: 'Europe/Lisbon' }).slice(0, 16).replace(' ', 'T');
  };

  const [form, setForm] = useState({ staff_id: '', start_time: '', end_time: '', reason: '' });

  useEffect(() => {
    if (open) {
      if (editingBlock) {
        setForm({ staff_id: editingBlock.staff_id || '', start_time: toLocalInput(editingBlock.start_time), end_time: toLocalInput(editingBlock.end_time), reason: editingBlock.reason || '' });
      } else if (initialValues) {
        setForm(initialValues);
      } else {
        setForm({ staff_id: '', start_time: '', end_time: '', reason: '' });
      }
    }
  }, [open, editingBlock, initialValues]);

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="schedule-dialog-content schedule-dialog-sheet block-modal-content sm:max-w-[500px]">
        <DialogHeader className="schedule-dialog-header">
          <DialogTitle className="text-[#1A1815] text-lg sm:text-xl leading-snug">
            {editingBlock ? (language === 'pt' ? 'Editar Bloqueio' : 'Edit Block') : (language === 'pt' ? 'Bloquear Horário' : 'Block Time')}
          </DialogTitle>
        </DialogHeader>
        <div className="schedule-dialog-scroll space-y-4">
          <div>
            <label className="text-sm text-[#635E54]">{language === 'pt' ? 'Motivo' : 'Reason'} *</label>
            <Input value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
              placeholder={language === 'pt' ? 'Ex: Almoço, Reunião, Folga...' : 'Ex: Lunch, Meeting, Day off...'} className="mt-1" />
          </div>
          <div>
            <label className="text-sm text-[#635E54]">{language === 'pt' ? 'Profissional (opcional)' : 'Professional (optional)'}</label>
            <Select value={form.staff_id || 'all'} onValueChange={v => setForm(f => ({ ...f, staff_id: v === 'all' ? '' : v }))}>
              <SelectTrigger className="mt-1"><SelectValue placeholder={language === 'pt' ? 'Todos' : 'All'} /></SelectTrigger>
              <SelectContent className="schedule-select-content">
                <SelectItem value="all">{language === 'pt' ? 'Todos os profissionais' : 'All professionals'}</SelectItem>
                {staff.filter(s => s.is_active).map(member => <SelectItem key={member.staff_id} value={member.staff_id}>{member.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 schedule-modal-field-grid">
            <div>
              <label className="text-sm text-[#635E54]">{language === 'pt' ? 'Início' : 'Start'} *</label>
              <Input type="datetime-local" value={form.start_time} onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <label className="text-sm text-[#635E54]">{language === 'pt' ? 'Fim' : 'End'} *</label>
              <Input type="datetime-local" value={form.end_time} onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))} className="mt-1" />
            </div>
          </div>
          {editingBlock ? (
            <div className="grid grid-cols-2 gap-2 schedule-modal-field-grid">
              <Button onClick={() => onSave(form)} className="flex-1 bg-[#BFA05F] text-white hover:bg-[#9E8246] rounded-full">
                <Check className="w-4 h-4 mr-2" />{language === 'pt' ? 'Guardar' : 'Save'}
              </Button>
              <Button onClick={() => onDelete(editingBlock.block_id)} variant="outline" className="min-w-0 rounded-full text-red-600 hover:bg-red-50 border-red-300">
                <Trash2 className="w-4 h-4 mr-2" />{language === 'pt' ? 'Eliminar' : 'Delete'}
              </Button>
            </div>
          ) : (
            <Button onClick={() => onSave(form)} className="w-full bg-red-500 text-white hover:bg-red-600 rounded-full">
              <Ban className="w-4 h-4 mr-2" />{language === 'pt' ? 'Bloquear Horário' : 'Block Time'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
});


// ============================================================
// MobileFocusWeek — 7 dias, foco dinâmico, sem scroll horizontal
// Desenho independente: a grelha original de PC/Dia não é alterada.
// ============================================================
const MobileFocusWeek = memo(({
  weekDays, currentDate, language, locale, isStaff, selectedStaffId, getBookingsForDay,
  getBlockedSlotsForDay, getService, getStatusColor, formatTime,
  onSelectDay, onOpenBooking, onOpenBlock, onEmptySlot, onBookingAction
}) => {
  const scrollRef = useRef(null);
  const hourHeight = 96; // 48px por meia hora: permite texto legível sem alterar a duração dos serviços.
  const [uniform, setUniform] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const hoursCount = END_HOUR - START_HOUR;
  const totalMinutes = hoursCount * 60;
  const focusedIndex = Math.max(0, weekDays.findIndex(day => isSameDay(day, currentDate)));
  const columns = `39px ${weekDays.map((_, index) => !uniform && index === focusedIndex ? '2.8fr' : '1fr').join(' ')}`;
  const totalHeight = hoursCount * hourHeight;
  const halfHourHeight = hourHeight / 2;
  const lines = `repeating-linear-gradient(to bottom, #D9D0C1 0px, #D9D0C1 1px, transparent 1px, transparent ${hourHeight}px), repeating-linear-gradient(to bottom, #ECE7DE 0px, #ECE7DE 1px, transparent 1px, transparent ${halfHourHeight}px)`;
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);
  const selectColumn = (day, event) => {
    if (event) event.stopPropagation();
    if (uniform) setUniform(false);
    if (!isSameDay(day, currentDate)) onSelectDay(day);
  };
  const selectEmptySlot = (day, event) => {
    if (!isSameDay(day, currentDate)) { selectColumn(day, event); return; }
    if (!isStaff) return;
    const y = event.clientY - event.currentTarget.getBoundingClientRect().top;
    const halfHoursFromStart = Math.max(0, Math.min(hoursCount * 2 - 1, Math.floor(y / (hourHeight / 2))));
    onEmptySlot(day, halfHoursFromStart);
  };
  const bookingGeometry = booking => {
    const { hour, minute } = getLisbonHourMinute(booking.start_time);
    const start = (hour - START_HOUR) * 60 + minute;
    const service = getService(booking.service_id);
    const duration = service?.duration || 60;
    const buffer = service?.buffer_time ?? 15;
    if (start >= totalMinutes || start + duration + buffer <= 0) return null;
    const begin = Math.max(0, start);
    const endService = Math.min(totalMinutes, start + duration);
    const endBuffer = Math.min(totalMinutes, start + duration + buffer);
    return {
      top: begin * hourHeight / 60,
      serviceHeight: Math.max(0, (endService - begin) * hourHeight / 60),
      bufferTop: Math.max(0, endService * hourHeight / 60),
      bufferHeight: Math.max(0, (endBuffer - Math.max(0, endService)) * hourHeight / 60)
    };
  };
  const blockGeometry = (block, day) => {
    const dayKey = lisbonDayKey(day);
    const startKey = lisbonDayKey(block.start_time);
    const endKey = lisbonDayKey(block.end_time);
    const startParts = getLisbonHourMinute(block.start_time);
    const endParts = getLisbonHourMinute(block.end_time);
    const start = (startKey < dayKey ? 0 : startParts.hour * 60 + startParts.minute) - START_HOUR * 60;
    const end = (endKey > dayKey ? 1440 : endParts.hour * 60 + endParts.minute) - START_HOUR * 60;
    const begin = Math.max(0, start);
    const finish = Math.min(totalMinutes, end);
    if (finish <= begin) return null;
    return { top: begin * hourHeight / 60, height: (finish - begin) * hourHeight / 60 };
  };
  const currentParts = getLisbonHourMinute(now);
  const nowTop = ((currentParts.hour - START_HOUR) * 60 + currentParts.minute) * hourHeight / 60;
  return (
    <div className="schedule-mobile-focus-layout sm:hidden" data-testid="schedule-mobile-focus-week">
      <div className="schedule-mobile-focus-tools">
        <div className="flex items-center gap-1 min-w-0 text-[11px] text-[#635E54]">
          <CalendarRange className="w-3.5 h-3.5 text-[#BFA05F] shrink-0" />
          <span className="truncate">{language === 'pt' ? (uniform ? '7 dias' : 'Semana inteligente') : (uniform ? '7 days' : 'Smart week')}</span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button type="button" onClick={() => setUniform(value => !value)}
            aria-label={language === 'pt' ? (uniform ? 'Ativar foco num dia' : 'Mostrar sete dias uniformes') : (uniform ? 'Focus on a day' : 'Show uniform days')}
            className="text-[11px] font-semibold text-[#635E54] px-2 py-1 rounded-full border border-[#E6E2DA] bg-white">
            {language === 'pt' ? (uniform ? 'Focar' : 'Uniforme') : (uniform ? 'Focus' : 'Uniform')}
          </button>
        </div>
      </div>
      <div className="schedule-mobile-focus-headers" style={{ gridTemplateColumns: columns }}>
        <div className="flex justify-center items-center text-[10px] text-[#9B9387]" aria-hidden="true"><Clock className="w-3.5 h-3.5" /></div>
        {weekDays.map((day, index) => {
          const active = index === focusedIndex;
          const today = isSameDay(day, now);
          return (
            <button key={format(day, 'yyyy-MM-dd')} type="button" onClick={() => selectColumn(day)}
              aria-pressed={active} aria-label={`${format(day, 'EEEE d MMMM', { locale })}${active ? (language === 'pt' ? ', em foco' : ', focused') : ''}`}
              className={`schedule-mobile-focus-day ${active ? 'is-active' : ''} ${today ? 'is-today' : ''}`}>
              <span className="schedule-mobile-focus-dayname">{format(day, 'EEE', { locale }).replace('.', '')}</span>
              <span className="schedule-mobile-focus-date">{format(day, 'd')}</span>
            </button>
          );
        })}
      </div>
      <div ref={scrollRef} className="schedule-mobile-focus-scroll" data-testid="schedule-mobile-week-scroll">
        <div className="schedule-mobile-focus-grid" style={{ gridTemplateColumns: columns, minHeight: totalHeight }}>
          <div className="schedule-mobile-focus-rail" style={{ height: totalHeight, backgroundImage: lines }} aria-hidden="true">
            {Array.from({ length: hoursCount * 2 }, (_, halfHourIndex) => (
              <span key={halfHourIndex} className={`absolute right-1 leading-3 text-[#746F66] ${halfHourIndex % 2 === 0 ? 'text-[9px] font-semibold' : 'text-[9px] font-normal'}`}
                style={{ top: halfHourIndex * halfHourHeight + 3 }}>
                {`${String(START_HOUR + Math.floor(halfHourIndex / 2)).padStart(2, '0')}:${halfHourIndex % 2 === 0 ? '00' : '30'}`}
              </span>
            ))}
          </div>
          {weekDays.map((day, index) => {
            const active = index === focusedIndex;
            const today = isSameDay(day, now);
            const appointments = getBookingsForDay(day);
            const blockages = getBlockedSlotsForDay(day).filter(block => !selectedStaffId || !block.staff_id || block.staff_id === selectedStaffId);
            return (
              <div key={format(day, 'yyyy-MM-dd')}
                className={`schedule-mobile-focus-track ${active ? 'is-active' : ''} ${today ? 'is-today' : ''}`}
                style={{ height: totalHeight, backgroundImage: lines }}
                onClick={event => selectEmptySlot(day, event)}
                aria-label={`${format(day, 'EEEE d MMMM', { locale })}: ${appointments.length} ${language === 'pt' ? 'marcações' : 'bookings'}`}>
                {blockages.map(block => {
                  const box = blockGeometry(block, day);
                  if (!box) return null;
                  return (
                    <button key={block.block_id} type="button"
                      title={`${language === 'pt' ? 'Bloqueio' : 'Blocked'}: ${block.reason || ''}`}
                      aria-label={`${language === 'pt' ? 'Bloqueio' : 'Blocked'} ${block.reason || ''}`}
                      className={`schedule-mobile-focus-block ${!uniform && active ? 'is-focused' : ''}`} style={{ top: box.top, height: box.height, zIndex: 4 }}
                      onClick={event => { event.stopPropagation(); onSelectDay(day); onOpenBlock(block); }}>
                      <span className="schedule-mobile-focus-block-reason">{block.reason || (language === 'pt' ? 'Bloqueado' : 'Blocked')}</span>
                    </button>
                  );
                })}
                {appointments.map(booking => {
                  const box = bookingGeometry(booking);
                  if (!box) return null;
                  const service = getService(booking.service_id);
                  const colors = getStatusColor(booking.status);
                  const fullName = booking.user?.name || booking.guest_snapshot?.name || booking.guest_name || 'Cliente';
                  const firstName = String(fullName).trim().split(/\s+/)[0] || 'Cliente';
                  const time = formatTime(booking.start_time);
                  const serviceName = language === 'pt' ? service?.name_pt || 'Serviço' : service?.name_en || 'Service';
                  const price = booking.price ?? service?.price;
                  const compactPrice = price == null ? '—' : `${MOBILE_PRICE_FORMATTER.format(Number(price) || 0)}€`;
                  const description = `${time} · ${fullName} · ${serviceName} · ${price == null ? 'Preço indisponível' : euro(price)}`;
                  const expanded = !uniform && active;
                  return (
                    <React.Fragment key={booking.booking_id}>
                      {box.bufferHeight > 0 && <div className="schedule-mobile-focus-buffer" style={{ top: box.bufferTop, height: box.bufferHeight, zIndex: 5 }} aria-hidden="true" />}
                      {box.serviceHeight > 0 && (
                        <div role="button" tabIndex={0} className={`schedule-mobile-focus-booking ${expanded ? 'is-focused' : ''} ${colors.bg} ${colors.border} ${colors.text}`}
                          style={{ top: box.top, height: box.serviceHeight, zIndex: 7 }}
                          title={description} aria-label={description}
                          onClick={event => { event.stopPropagation(); onSelectDay(day); onOpenBooking(booking); }}
                          onKeyDown={event => { if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); event.stopPropagation(); onSelectDay(day); onOpenBooking(booking); } }}>
                          <span className="schedule-mobile-focus-name">{expanded ? fullName : firstName}</span>
                          <span className="schedule-mobile-focus-hour">{time}</span>
                          {expanded && <span className="schedule-mobile-focus-service">{serviceName}</span>}
                          <span className="schedule-mobile-focus-price">{compactPrice}</span>
                          {isStaff && expanded && booking.status === 'pending' && box.serviceHeight >= 44 && (
                            <div className="absolute bottom-0.5 left-1 flex gap-1" onClick={event => event.stopPropagation()}>
                              <button type="button" title={language === 'pt' ? 'Confirmar marcação' : 'Confirm booking'} aria-label={language === 'pt' ? 'Confirmar marcação' : 'Confirm booking'}
                                onClick={event => { event.stopPropagation(); onBookingAction(booking.booking_id, 'confirmed'); }}
                                className="flex h-4 w-4 items-center justify-center rounded bg-white/60 hover:bg-white/90">
                                <Check className="h-3 w-3" />
                              </button>
                              <button type="button" title={language === 'pt' ? 'Cancelar marcação' : 'Cancel booking'} aria-label={language === 'pt' ? 'Cancelar marcação' : 'Cancel booking'}
                                onClick={event => { event.stopPropagation(); if (window.confirm(language === 'pt' ? 'Cancelar esta marcação?' : 'Cancel this booking?')) onBookingAction(booking.booking_id, 'cancelled'); }}
                                className="flex h-4 w-4 items-center justify-center rounded bg-white/60 hover:bg-white/90">
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
                {today && nowTop >= 0 && nowTop <= totalHeight && <div className="schedule-mobile-focus-now" style={{ top: nowTop }} aria-hidden="true" />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
});

// ============================================================
// COMPONENTE PRINCIPAL
// ============================================================
const SchedulePage = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const { settings } = useSiteSettings();
  const navigate = useNavigate();
  const locale = language === 'pt' ? pt : enGB;

  const [viewMode, setViewMode] = useState('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedStaffId, setSelectedStaffId] = useState(null);
  const [staff, setStaff] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [clients, setClients] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [masterLoading, setMasterLoading] = useState(true);
  const [scheduleLoading, setScheduleLoading] = useState(true);
  const [masterError, setMasterError] = useState(false);
  const [scheduleError, setScheduleError] = useState(false);
  const [hasScheduleLoaded, setHasScheduleLoaded] = useState(false);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientsLoaded, setClientsLoaded] = useState(false);
  const [cancelledCount, setCancelledCount] = useState(0);
  const [showCancelled, setShowCancelled] = useState(false);
  // Pesquisa global em marcações da cliente, independentemente do período visível.
  // Recolhida por omissão para não ocupar espaço útil na Agenda.
  const [clientBookingSearchOpen, setClientBookingSearchOpen] = useState(false);
  const [clientBookingQuery, setClientBookingQuery] = useState('');
  const [clientBookingResults, setClientBookingResults] = useState([]);
  const [clientBookingSearchLoading, setClientBookingSearchLoading] = useState(false);
  const [clientBookingSearchError, setClientBookingSearchError] = useState('');
  const [clientBookingIncludePast, setClientBookingIncludePast] = useState(false);
  const [activeClientBookingFilter, setActiveClientBookingFilter] = useState(null);
  const [draggedBooking, setDraggedBooking] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const highlightedSlotRef = useRef(null);
  const bookingRequestRef = useRef(0);
  const blockRequestRef = useRef(0);
  const clientsPendingRef = useRef(null);
  const clientsLoadedRef = useRef(false);
  const weekLayoutRef = useRef(null);
  const weekHeaderRef = useRef(null);
  const weekTimeRef = useRef(null);
  const weekBoardRef = useRef(null);
  const [weekScrollVersion, setWeekScrollVersion] = useState(0);
  const syncWeekHorizontal = useCallback(event => {
    const board = weekBoardRef.current;
    if (board) board.scrollLeft = event.currentTarget.scrollLeft;
  }, []);
  const syncWeekVertical = useCallback(event => {
    const board = weekBoardRef.current;
    if (board) board.scrollTop = event.currentTarget.scrollTop;
  }, []);
  const syncWeekDesktopScroll = useCallback(event => {
    if (window.matchMedia('(max-width: 639px)').matches) return;
    const header = weekHeaderRef.current;
    const rail = weekTimeRef.current;
    if (header && header.scrollLeft !== event.currentTarget.scrollLeft) header.scrollLeft = event.currentTarget.scrollLeft;
    if (rail && rail.scrollTop !== event.currentTarget.scrollTop) rail.scrollTop = event.currentTarget.scrollTop;
  }, []);
  useEffect(() => {
    // Captura instalada uma única vez: os refs podem estar temporariamente vazios
    // enquanto a agenda carrega ou quando se muda entre Dia/Semana/Mês.
    const handleDesktopWheel = event => {
      if (window.matchMedia('(max-width: 639px)').matches || event.ctrlKey || event.metaKey || !event.deltaY) return;
      const layout = weekLayoutRef.current;
      const board = weekBoardRef.current;
      if (!layout || !board || !layout.contains(event.target)) return;
      const multiplier = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? board.clientHeight : 1;
      const maxScroll = Math.max(0, board.scrollHeight - board.clientHeight);
      const next = Math.min(maxScroll, Math.max(0, board.scrollTop + event.deltaY * multiplier));
      // Mesmo nos extremos, não deixar a roda deslocar o documento e esconder os controlos sob o cabeçalho fixo.
      event.preventDefault();
      if (Math.abs(next - board.scrollTop) < 0.01) return;
      board.scrollTop = next;
      const rail = weekTimeRef.current;
      if (rail && rail.scrollTop !== next) rail.scrollTop = next;
    };
    window.addEventListener('wheel', handleDesktopWheel, { passive: false, capture: true });
    return () => window.removeEventListener('wheel', handleDesktopWheel, true);
  }, []);

  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [bookingSaving, setBookingSaving] = useState(false);
  const [bookingInitialTime, setBookingInitialTime] = useState('');
  const [bookingStartAsExisting, setBookingStartAsExisting] = useState(false);
  const [nextBookingSource, setNextBookingSource] = useState(null);
  const [undoCompletionBusy, setUndoCompletionBusy] = useState(false);
  const [confirmStatusBusy, setConfirmStatusBusy] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  const [blockInitialValues, setBlockInitialValues] = useState(null);
  const [slotActionMenuOpen, setSlotActionMenuOpen] = useState(false);
  const [selectedSlotInfo, setSelectedSlotInfo] = useState(null);

  const isAdmin = user?.role === 'admin';
  const isStaff = user?.role === 'staff' || isAdmin;

  const visibleRange = useMemo(() => {
    const date = currentDate instanceof Date && !isNaN(currentDate.getTime()) ? currentDate : new Date();
    let from, until;
    if (viewMode === 'day') {
      from = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      until = addDays(from, 1);
    } else if (viewMode === 'month') {
      from = startOfWeek(startOfMonth(date), { weekStartsOn: 1 });
      const lastVisibleDay = endOfWeek(endOfMonth(date), { weekStartsOn: 1 });
      until = addDays(new Date(lastVisibleDay.getFullYear(), lastVisibleDay.getMonth(), lastVisibleDay.getDate()), 1);
    } else {
      from = startOfWeek(date, { weekStartsOn: 1 });
      until = addDays(from, 7);
    }
    return { start_date: from.toISOString(), end_date: until.toISOString() };
  }, [currentDate, viewMode]);
  const rangeRef = useRef(visibleRange);
  rangeRef.current = visibleRange;

  const loadBookings = useCallback(async () => {
    const id = ++bookingRequestRef.current;
    const range = rangeRef.current;
    const { data } = await api.get('/admin/bookings', { params: range });
    if (id === bookingRequestRef.current && range.start_date === rangeRef.current.start_date && range.end_date === rangeRef.current.end_date) setBookings(data);
    return data;
  }, []);
  useEffect(() => {
    const query = clientBookingQuery.trim();
    if (!clientBookingSearchOpen || query.length < 2 || !isStaff) {
      setClientBookingResults([]);
      setClientBookingSearchError('');
      setClientBookingSearchLoading(false);
      return;
    }
    let alive = true;
    setClientBookingSearchLoading(true);
    setClientBookingSearchError('');
    const timer = setTimeout(() => {
      api.get('/admin/bookings/search-client', { params: { q: query, include_past: clientBookingIncludePast } })
        .then(({ data }) => { if (alive) setClientBookingResults(data.items || []); })
        .catch(() => { if (alive) { setClientBookingResults([]); setClientBookingSearchError(language === 'pt' ? 'Erro ao pesquisar marcações.' : 'Failed to search bookings.'); } })
        .finally(() => { if (alive) setClientBookingSearchLoading(false); });
    }, 350);
    return () => { alive = false; clearTimeout(timer); };
  }, [clientBookingSearchOpen, clientBookingQuery, clientBookingIncludePast, isStaff, language]);

  const jumpToClientBooking = useCallback((result) => {
    const dt = toUTCDate(result.start_time);
    if (!dt) return;
    const dateKey = lisbonDayKey(dt);
    const [y, m, d] = dateKey.split('-').map(Number);
    if (!y || !m || !d) return;
    setCurrentDate(new Date(y, m - 1, d, 12));
    setViewMode('day');
    setShowCancelled(false);
    if (isAdmin && result.staff_id) setSelectedStaffId(result.staff_id);
    if (result.user_id) setActiveClientBookingFilter({ user_id: result.user_id, name: result.client_name });
    setClientBookingQuery('');
    setClientBookingResults([]);
    setClientBookingSearchOpen(false);
  }, [isAdmin]);

  const loadBlocks = useCallback(async () => {
    const id = ++blockRequestRef.current;
    const range = rangeRef.current;
    const { data } = await api.get('/admin/blocked-slots', { params: range });
    if (id === blockRequestRef.current && range.start_date === rangeRef.current.start_date && range.end_date === rangeRef.current.end_date) setBlockedSlots(data);
    return data;
  }, []);
  const loadCancelledCount = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/bookings/cancelled-count');
      setCancelledCount(data.count);
    } catch (error) { console.error('Erro no contador de cancelados:', error); }
  }, []);
  const loadClients = useCallback(async (force = false) => {
    if (clientsLoadedRef.current && !force) return;
    if (clientsPendingRef.current) return clientsPendingRef.current;
    setClientsLoading(true);
    const pending = api.get('/admin/clients')
      .then(({ data }) => {
        setClients(data.filter(c => c.role === 'client').sort((a, b) => (a.name || '').localeCompare(b.name || '')));
        clientsLoadedRef.current = true;
        setClientsLoaded(true);
      })
      .catch(error => {
        console.error('Erro ao carregar clientes:', error);
        toast.error('Não foi possível carregar a lista de clientes.');
      })
      .finally(() => { clientsPendingRef.current = null; setClientsLoading(false); });
    clientsPendingRef.current = pending;
    return pending;
  }, []);
  const loadMaster = useCallback(async () => {
    setMasterLoading(true);
    setMasterError(false);
    try {
      const [staffRes, servicesRes] = await Promise.all([api.get('/staff'), api.get('/services')]);
      setStaff(staffRes.data.filter(s => s.is_active));
      setServices(servicesRes.data);
      if (user?.role === 'staff') {
        const myStaff = staffRes.data.find(s => s.email === user.email);
        if (myStaff) setSelectedStaffId(myStaff.staff_id);
      }
    } catch (error) {
      console.error('Erro ao carregar serviços e equipa:', error);
      setMasterError(true);
      toast.error('Não foi possível carregar os serviços e colaboradores.');
    } finally { setMasterLoading(false); }
  }, [user?.role, user?.email]);
  const refreshVisibleData = useCallback(async () => {
    await Promise.all([loadBookings(), loadBlocks()]);
  }, [loadBookings, loadBlocks]);
  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    loadMaster();
    if (user.role === 'admin') loadCancelledCount();
  }, [user?.user_id, user?.role, loadMaster, loadCancelledCount, navigate]);
  useEffect(() => {
    if (!user) return;
    let alive = true;
    setScheduleLoading(true);
    setScheduleError(false);
    refreshVisibleData()
      .then(() => { if (alive) setHasScheduleLoaded(true); })
      .catch(error => {
        if (alive) { console.error('Erro ao carregar agenda:', error); setScheduleError(true); toast.error('Não foi possível carregar a agenda deste período.'); }
      })
      .finally(() => { if (alive) setScheduleLoading(false); });
    return () => { alive = false; };
  }, [user?.user_id, visibleRange.start_date, visibleRange.end_date, refreshVisibleData]);
  useEffect(() => {
    if (bookingDialogOpen || editMode) loadClients();
  }, [bookingDialogOpen, editMode, loadClients]);

  const navigateDate = (direction) => {
    const amount = direction === 'prev' ? -1 : 1;
    if (viewMode === 'day') setCurrentDate(addDays(currentDate, amount));
    else if (viewMode === 'month') setCurrentDate(addMonths(currentDate, amount));
    else setCurrentDate(addWeeks(currentDate, amount));
  };
  const goToToday = () => { setCurrentDate(new Date()); setWeekScrollVersion(v => v + 1); };

  const weekDays = useMemo(() => {
    const start = startOfWeek(currentDate, { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end: endOfWeek(currentDate, { weekStartsOn: 1 }) });
  }, [currentDate]);
  const weekStartKey = useMemo(() => format(startOfWeek(currentDate, { weekStartsOn: 1 }), 'yyyy-MM-dd'), [currentDate]);
  useLayoutEffect(() => {
    if (viewMode !== 'week' || masterLoading || !hasScheduleLoaded) return;
    const header = weekHeaderRef.current;
    const board = weekBoardRef.current;
    if (!header || !board) return;
    const positionWeek = () => {
      if (weekHeaderRef.current !== header || weekBoardRef.current !== board) return;
      const firstDay = header.querySelector('[data-schedule-day-index="0"]');
      if (!firstDay) return;
      const today = new Date();
      const currentWeekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
      const dayIndex = viewMode === 'week' && isSameDay(currentWeekStart, startOfWeek(today, { weekStartsOn: 1 })) ? (today.getDay() + 6) % 7 : 0;
      const targetDay = header.querySelector(`[data-schedule-day-index="${dayIndex}"]`);
      const target = targetDay ? targetDay.offsetLeft - firstDay.offsetLeft : 0;
      header.scrollLeft = Math.max(0, Math.min(target, header.scrollWidth - header.clientWidth));
      board.scrollLeft = header.scrollLeft;
    };
    const alignDesktopHeader = () => {
      const width = window.matchMedia('(max-width: 639px)').matches ? 0 : board.offsetWidth - board.clientWidth;
      header.style.paddingRight = `${Math.max(0, width)}px`;
    };
    alignDesktopHeader();
    positionWeek();
    let previousWidth = header.clientWidth;
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => {
      alignDesktopHeader();
      if (header.clientWidth !== previousWidth) {
        previousWidth = header.clientWidth;
        positionWeek();
      }
    }) : null;
    if (observer) observer.observe(header);
    else window.addEventListener('resize', positionWeek);
    return () => {
      if (observer) observer.disconnect();
      else window.removeEventListener('resize', positionWeek);
    };
  }, [viewMode, weekStartKey, masterLoading, hasScheduleLoaded, weekScrollVersion]);

  const filteredBookings = useMemo(() => bookings.filter(b => {
    if (activeClientBookingFilter && b.user_id !== activeClientBookingFilter.user_id) return false;
    if (selectedStaffId && b.staff_id !== selectedStaffId) return false;
    if (!showCancelled && b.status === 'cancelled') return false;
    if (showCancelled && b.status !== 'cancelled') return false;
    return true;
  }), [bookings, selectedStaffId, showCancelled, activeClientBookingFilter]);
  const visibleDays = useMemo(() => eachDayOfInterval({
    start: new Date(visibleRange.start_date),
    end: addDays(new Date(visibleRange.end_date), -1)
  }), [visibleRange.start_date, visibleRange.end_date]);
  const bookingsByDay = useMemo(() => {
    const byDay = new Map();
    filteredBookings.forEach(booking => {
      const key = lisbonDayKey(booking.start_time);
      if (!byDay.has(key)) byDay.set(key, []);
      byDay.get(key).push(booking);
    });
    return byDay;
  }, [filteredBookings]);
  const blockedByDay = useMemo(() => {
    const byDay = new Map();
    visibleDays.forEach(day => {
      const key = lisbonDayKey(day);
      byDay.set(key, blockedSlots.filter(block => {
        const start = toUTCDate(block.start_time);
        const end = toUTCDate(block.end_time);
        const nextDay = addDays(day, 1);
        return start && end && start < nextDay && end > day;
      }));
    });
    return byDay;
  }, [blockedSlots, visibleDays]);
  const servicesMap = useMemo(() => new Map(services.map(s => [s.service_id, s])), [services]);
  const sortedServices = useMemo(() => [...services].sort((a, b) => (
    (language === 'pt' ? a.name_pt : a.name_en || '').localeCompare(language === 'pt' ? b.name_pt : b.name_en || '')
  )), [services, language]);
  const getService = useCallback(id => servicesMap.get(id), [servicesMap]);

  const getBlockStyle = useCallback((booking) => {
    const { hour: startHour, minute: startMinute } = getLisbonHourMinute(booking.start_time);
    const service = getService(booking.service_id);
    const duration = service?.duration || 60;
    const bufferTime = service?.buffer_time ?? 15;
    const minutesPerSlot = viewMode === 'day' ? 15 : 30;
    const minutesFromStart = (startHour - START_HOUR) * 60 + startMinute;
    return {
      top: (minutesFromStart / minutesPerSlot) * SLOT_HEIGHT,
      height: ((duration + bufferTime) / minutesPerSlot) * SLOT_HEIGHT,
      serviceHeight: (duration / minutesPerSlot) * SLOT_HEIGHT,
      duration, bufferTime
    };
  }, [viewMode, getService]);

  const formatTime = useCallback((isoString) => formatLisbon(isoString), []);

  const getEndTime = useCallback((booking) => {
    const d = toUTCDate(booking.start_time);
    if (!d) return '--:--';
    const service = getService(booking.service_id);
    return LISBON_TIME_FORMATTER.format(new Date(d.getTime() + (service?.duration || 60) * 60000));
  }, [getService]);

  const getBookingsForDay = useCallback(day => bookingsByDay.get(lisbonDayKey(day)) || EMPTY_ITEMS, [bookingsByDay]);
  const getBlockedSlotsForDay = useCallback(day => blockedByDay.get(lisbonDayKey(day)) || EMPTY_ITEMS, [blockedByDay]);

  const handleBookingAction = useCallback(async (bookingId, action) => {
    try {
      await api.put(`/admin/bookings/${bookingId}/status`, { status: action });
      setBookings(prev => prev.map(b => b.booking_id === bookingId ? { ...b, status: action } : b));
      toast.success(action === 'confirmed' ? 'Marcação confirmada!' : 'Marcação cancelada');
      if (action === 'cancelled') loadCancelledCount();
    } catch { toast.error('Erro ao atualizar marcação'); }
  }, [loadCancelledCount]);

  const handleSlotDoubleClick = useCallback((day, slotIndex) => {
    if (!isStaff) return;
    const slotsPerHour = viewMode === 'day' ? 4 : 2;
    const minutesPerSlot = 60 / slotsPerHour;
    const hour = START_HOUR + Math.floor(slotIndex / slotsPerHour);
    const minutes = (slotIndex % slotsPerHour) * minutesPerSlot;
    let slotDateTime = new Date(day);
    slotDateTime = setHours(slotDateTime, hour);
    slotDateTime = setMinutes(slotDateTime, minutes);
    slotDateTime.setSeconds(0); slotDateTime.setMilliseconds(0);
    setSelectedSlotInfo({ day, slotIndex, dateTime: slotDateTime, formattedDateTime: format(slotDateTime, "yyyy-MM-dd'T'HH:mm") });
    setSlotActionMenuOpen(true);
  }, [isStaff, viewMode]);

  const handleSlotActionSelect = useCallback((action) => {
    if (!selectedSlotInfo) return;
    const lourdyane = staff.find(s => s.name.toLowerCase().includes('lourdyane') || s.name.toLowerCase().includes('neves'));
    if (action === 'existing-client' || action === 'new-client') {
      setNextBookingSource(null);
      setBookingInitialTime(selectedSlotInfo.formattedDateTime);
      setBookingStartAsExisting(action === 'existing-client');
      setBookingDialogOpen(true);
    } else if (action === 'block') {
      const endDateTime = new Date(selectedSlotInfo.dateTime);
      endDateTime.setHours(endDateTime.getHours() + 1);
      setEditingBlock(null);
      setBlockInitialValues({ staff_id: lourdyane?.staff_id || '', start_time: selectedSlotInfo.formattedDateTime, end_time: format(endDateTime, "yyyy-MM-dd'T'HH:mm"), reason: '' });
      setBlockDialogOpen(true);
    }
    setSlotActionMenuOpen(false);
    setSelectedSlotInfo(null);
  }, [selectedSlotInfo, staff]);

  const checkConflict = useCallback((proposedStart, durationMinutes, staffId, excludeBookingId = null) => {
    const start = new Date(proposedStart);
    const end = new Date(start.getTime() + durationMinutes * 60000);
    const hit = bookings.find(b => {
      if (b.status === 'cancelled') return false;
      if (excludeBookingId && b.booking_id === excludeBookingId) return false;
      if (staffId && b.staff_id && b.staff_id !== staffId) return false;
      const bStart = toUTCDate(b.start_time);
      const bService = getService(b.service_id);
      const bEnd = new Date(bStart.getTime() + ((bService?.duration || 60) + (bService?.buffer_time ?? 15)) * 60000);
      return start < bEnd && end > bStart;
    });
    if (hit) {
      const svc = getService(hit.service_id);
      const who = hit.user?.name || hit.guest_name || 'Cliente';
      return { conflict: true, reason: language === 'pt' ? `Conflito com ${who} às ${formatLisbon(hit.start_time)} — ${svc?.name_pt || ''}` : `Conflict with ${who} at ${formatLisbon(hit.start_time)} — ${svc?.name_en || ''}` };
    }
    const block = blockedSlots.find(bl => {
      if (staffId && bl.staff_id && bl.staff_id !== staffId) return false;
      const bStart = toUTCDate(bl.start_time);
      const bEnd = toUTCDate(bl.end_time);
      return start < bEnd && end > bStart;
    });
    if (block) return { conflict: true, reason: language === 'pt' ? `Horário bloqueado: "${block.reason}" (${formatLisbon(block.start_time)}–${formatLisbon(block.end_time)})` : `Blocked: "${block.reason}" (${formatLisbon(block.start_time)}–${formatLisbon(block.end_time)})` };
    return { conflict: false };
  }, [bookings, blockedSlots, getService, language]);

  const handleCreateBookingSubmit = useCallback(async ({ form, isNewClient, selectedClientId, selectedServices, adjustmentType, adjustmentPercent, multiDateMode = false, multiDateItems = [], batchId, sendConfirmationEmail = true, sendConfirmationWhatsapp = true }) => {
    if (bookingSaving) return;
    setBookingSaving(true);
    try {
      if (isNewClient ? (!form.guest_name || selectedServices.length === 0 || (!multiDateMode && !form.start_time)) : (!selectedClientId || selectedServices.length === 0 || (!multiDateMode && !form.start_time))) {
        toast.error(language === 'pt' ? 'Preencha todos os campos obrigatórios' : 'Fill in all required fields'); return;
      }
      const startTimeISO = multiDateMode ? null : datetimeLocalToUTC(form.start_time);
      const checkStart = startTimeISO ? new Date(startTimeISO) : null;
      let cumulative = 0;
      // REGRA 2: Admin/Staff podem sobrepor horários livremente — verificação de conflito removida do frontend
      // O backend permite sobreposição para admin/staff (skip_conflict=True)
      let clientData;
      if (!isNewClient && selectedClientId) {
        const client = clients.find(c => c.user_id === selectedClientId);
        if (!client) { toast.error(language === 'pt' ? 'Cliente não encontrado' : 'Client not found'); return; }
        clientData = { name: client.name, email: client.email || '', phone: client.phone || '' };
      } else {
        clientData = { name: form.guest_name, email: form.guest_email, phone: form.guest_phone };
      }
      const confirmationChoices = {
        send_confirmation_email: sendConfirmationEmail,
        send_confirmation_whatsapp: sendConfirmationWhatsapp,
      };
      const selectedChannels = [sendConfirmationWhatsapp && clientData.phone ? 'WhatsApp' : null,
        sendConfirmationEmail && clientData.email && !clientData.email.includes('@temp.lousway') ? 'email' : null].filter(Boolean);
      const creationFeedback = (num) => language === 'pt'
        ? `${num} ${num === 1 ? 'marcação criada' : 'marcações criadas'}!${selectedChannels.length ? ` Confirmação solicitada por ${selectedChannels.join(' e ')}.` : ' Sem confirmação imediata à cliente.'} Lembretes automáticos mantidos.`
        : `${num} booking(s) created!${selectedChannels.length ? ` Confirmation requested via ${selectedChannels.join(' and ')}.` : ' No immediate client confirmation.'} Automatic reminders remain enabled.`;
      if (multiDateMode) {
        if (!form.staff_id || !multiDateItems.length || !batchId) {
          toast.error(language === 'pt' ? 'Seleciona a profissional e pelo menos uma data com horário disponível.' : 'Choose the professional and at least one available date/time.');
          return;
        }
        const { data } = await api.post('/bookings/guest/multi-date', {
          batch_id: batchId,
          client_user_id: isNewClient ? null : selectedClientId,
          guest_name: clientData.name, guest_email: clientData.email, guest_phone: clientData.phone,
          staff_id: form.staff_id, service_ids: selectedServices.map(s => s.service_id),
          appointments: multiDateItems.map(i => ({ date: i.date, start_time: i.start_time })),
          notes: form.notes, adjustment_type: adjustmentType, adjustment_percent: adjustmentPercent,
          ...confirmationChoices,
        });
        toast.success(creationFeedback(data.appointments_created || multiDateItems.length));
      } else if (selectedServices.length > 1) {
        const startDateTime = new Date(startTimeISO);
        const bookingsPayload = selectedServices.map((service, index) => {
          let cum = 0;
          for (let i = 0; i < index; i++) cum += (selectedServices[i].duration || 60) + (selectedServices[i].buffer_time || 15);
          return { service_id: service.service_id, staff_id: form.staff_id || null, start_time: new Date(startDateTime.getTime() + cum * 60000).toISOString(), payment_method: 'in_store', notes: index === 0 ? form.notes : undefined };
        });
        await api.post('/bookings/guest/bulk', { bookings: bookingsPayload, guest_name: clientData.name, guest_email: clientData.email, guest_phone: clientData.phone, adjustment_type: adjustmentType, adjustment_percent: adjustmentPercent, ...confirmationChoices });
        toast.success(creationFeedback(selectedServices.length));
      } else {
        await api.post('/bookings/guest', { guest_name: clientData.name, guest_email: clientData.email, guest_phone: clientData.phone, service_id: selectedServices[0].service_id, staff_id: form.staff_id || null, start_time: startTimeISO, notes: form.notes, adjustment_type: adjustmentType, adjustment_percent: adjustmentPercent, ...confirmationChoices });
        toast.success(creationFeedback(1));
      }
      setBookingDialogOpen(false);
      setNextBookingSource(null);
      loadBookings().catch(error => { console.error('Erro a atualizar marcações:', error); toast.error('Agendamento criado, mas falhou a atualização visual da agenda.'); });
      if (isNewClient) loadClients(true);
    } catch (error) {
      let msg = language === 'pt' ? 'Erro ao criar agendamento' : 'Error creating booking';
      if (error.response?.data) { const d = error.response.data; if (typeof d === 'string') msg = d; else if (d.detail) { const det = d.detail; if (typeof det === 'string') msg = det; else if (Array.isArray(det)) msg = det.map(e => typeof e === 'string' ? e : e?.msg || '').filter(Boolean).join(', '); } else if (d.msg) msg = d.msg; } else if (error.message) msg = error.message;
      toast.error(String(msg));
    } finally {
      setBookingSaving(false);
    }
  }, [clients, language, loadBookings, loadClients, bookingSaving]);

  const handleBookingDoubleClick = useCallback((booking) => {
    setSelectedBooking(booking);
    setEditMode(false);
    setDetailsDialogOpen(true);
  }, []);

  const startNextBooking = useCallback(() => {
    if (!selectedBooking || !isStaff) return;
    setNextBookingSource(selectedBooking);
    setBookingInitialTime('');
    setBookingStartAsExisting(true);
    setEditMode(false);
    setDetailsDialogOpen(false);
    setBookingDialogOpen(true);
  }, [selectedBooking, isStaff]);

  const reopenCompletedBooking = useCallback(async () => {
    if (!selectedBooking || selectedBooking.status !== 'completed' || undoCompletionBusy) return;
    const msg = language === 'pt'
      ? 'Voltar esta marcação a CONFIRMADA? O pagamento será colocado como PENDENTE e os pontos atribuídos nesta conclusão serão descontados. Usa esta opção apenas se ainda não recebeste o pagamento.'
      : 'Return this booking to CONFIRMED? The payment will become PENDING and any points credited for this completion will be reversed. Only use this if payment was not received.';
    if (!window.confirm(msg)) return;
    setUndoCompletionBusy(true);
    try {
      const response = await api.put(`/admin/bookings/${selectedBooking.booking_id}/reopen-completion`, {});
      const updated = { ...selectedBooking, status: 'confirmed', payment_status: 'pending' };
      setBookings(prev => prev.map(b => b.booking_id === selectedBooking.booking_id ? { ...b, status: 'confirmed', payment_status: 'pending' } : b));
      setSelectedBooking(updated);
      toast.success(language === 'pt'
        ? `Marcação novamente confirmada. ${response.data?.points_reversed || 0} ponto(s) descontado(s).`
        : `Booking is confirmed again. ${response.data?.points_reversed || 0} point(s) reversed.`);
      loadBookings().catch(error => console.warn('Could not refresh bookings:', error));
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'pt' ? 'Não foi possível reverter a conclusão.' : 'Could not reopen booking.'));
    } finally { setUndoCompletionBusy(false); }
  }, [selectedBooking, undoCompletionBusy, language, loadBookings]);

  const handleEditBookingSave = useCallback(async (form) => {
    try {
      if (!form.service_id || !form.start_time) { toast.error(language === 'pt' ? 'Preencha todos os campos' : 'Fill in all fields'); return; }
      const startTimeISO = datetimeLocalToUTC(form.start_time);
      // REGRA 2: Admin/Staff podem sobrepor horários — verificação de conflito removida
      const adjustmentPayload = form.adjustment_changed
        ? { adjustment_type: form.adjustment_type, adjustment_percent: form.adjustment_percent }
        : {};
      const response = await api.put(`/admin/bookings/${selectedBooking.booking_id}/reschedule`, {
        start_time: startTimeISO,
        service_id: form.service_id,
        staff_id: form.staff_id || undefined,
        user_id: form.user_id || undefined,
        guest_name: form.guest_name || undefined,
        guest_email: form.guest_email || undefined,
        guest_phone: form.guest_phone || undefined,
        ...adjustmentPayload,
      });
      // Confirmar que o backend aplicou o desconto, para não dar sucesso enganador se estiver desatualizado.
      if (form.adjustment_changed && !response.data?.adjustment_saved) {
        setBookings(prev => prev.map(b => b.booking_id === selectedBooking.booking_id
          ? { ...b, start_time: startTimeISO, service_id: form.service_id || b.service_id, staff_id: form.staff_id || b.staff_id }
          : b));
        await loadBookings();
        toast.error(language === 'pt' ? 'Marcação alterada, mas o backend ainda não suporta guardar o desconto. Atualize o server.py antes de repetir.' : 'Booking updated, but the backend cannot save this adjustment yet. Update server.py first.');
        return;
      }
      // Atualização otimista: move a MESMA marcação de imediato (evita duplicado/original persistente)
      setBookings(prev => prev.map(b => b.booking_id === selectedBooking.booking_id
        ? { ...b, start_time: startTimeISO, service_id: form.service_id || b.service_id, staff_id: form.staff_id || b.staff_id,
            ...(form.adjustment_changed ? {
              adjustment_type: form.adjustment_type, adjustment_percent: form.adjustment_percent,
              price: response.data?.price, base_price: response.data?.base_price,
              adjustment_amount: response.data?.adjustment_amount, price_source: 'staff_manual'
            } : {}) }
        : b));
      toast.success(language === 'pt' ? 'Marcação atualizada!' : 'Booking updated!');
      setDetailsDialogOpen(false);
      setEditMode(false);
      loadBookings().catch(error => { console.error('Erro a atualizar marcação:', error); toast.error('Marcação guardada, mas falhou a atualização visual da agenda.'); });
    } catch (error) {
      let msg = language === 'pt' ? 'Erro ao atualizar' : 'Error updating';
      if (error.response?.data) { const d = error.response.data; if (typeof d === 'string') msg = d; else if (d.detail) { const det = d.detail; if (typeof det === 'string') msg = det; else if (Array.isArray(det)) msg = det.map(e => typeof e === 'string' ? e : e?.msg || '').filter(Boolean).join(', '); } else if (d.msg) msg = d.msg; } else if (error.message) msg = error.message;
      toast.error(String(msg));
    }
  }, [selectedBooking, language, loadBookings]);

  const handleSaveBlock = useCallback(async (form) => {
    if (!form.start_time || !form.end_time || !form.reason) { toast.error(language === 'pt' ? 'Preencha todos os campos obrigatórios' : 'Fill in all required fields'); return; }
    try {
      const payload = { staff_id: form.staff_id || null, start_time: datetimeLocalToUTC(form.start_time), end_time: datetimeLocalToUTC(form.end_time), reason: form.reason };
      if (editingBlock) { await api.put(`/admin/blocked-slots/${editingBlock.block_id}`, payload); toast.success(language === 'pt' ? 'Bloqueio atualizado!' : 'Block updated!'); }
      else { await api.post('/admin/blocked-slots', payload); toast.success(language === 'pt' ? 'Horário bloqueado!' : 'Time blocked!'); }
      setBlockDialogOpen(false); setEditingBlock(null);
      loadBlocks().catch(error => { console.error('Erro a atualizar bloqueios:', error); toast.error('Operação concluída, mas não foi possível atualizar os bloqueios.'); });
    } catch (error) {
      let msg = language === 'pt' ? 'Erro ao guardar bloqueio' : 'Error saving block';
      if (error.response?.data) { const d = error.response.data; if (typeof d === 'string') msg = d; else if (d.detail) { const det = d.detail; if (typeof det === 'string') msg = det; else if (Array.isArray(det)) msg = det.map(e => typeof e === 'string' ? e : e?.msg || '').filter(Boolean).join(', '); } else if (d.msg) msg = d.msg; } else if (error.message) msg = error.message;
      toast.error(String(msg));
    }
  }, [editingBlock, language, loadBlocks]);

  const handleDeleteBlockedSlot = useCallback(async (blockId) => {
    if (!window.confirm(language === 'pt' ? 'Eliminar este bloqueio?' : 'Delete this block?')) return;
    try {
      await api.delete(`/admin/blocked-slots/${blockId}`);
      toast.success(language === 'pt' ? 'Bloqueio removido!' : 'Block removed!');
      setBlockDialogOpen(false); setEditingBlock(null);
      loadBlocks().catch(error => { console.error('Erro a atualizar bloqueios:', error); toast.error('Operação concluída, mas não foi possível atualizar os bloqueios.'); });
    } catch { toast.error(language === 'pt' ? 'Erro ao remover bloqueio' : 'Error removing block'); }
  }, [language, loadBlocks]);

  const handleBlockDoubleClick = useCallback((block) => {
    if (!isStaff) return;
    setEditingBlock(block); setBlockInitialValues(null); setBlockDialogOpen(true);
  }, [isStaff]);

  const handleBlockDragStart = useCallback((e, block) => {
    if (!isStaff) { e.preventDefault(); return; }
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify({ type: 'block', block_id: block.block_id }));
    e.target.style.opacity = '0.5';
  }, [isStaff]);

  const handleBlockDrop = useCallback(async (e, day, slotIndex) => {
    e.preventDefault();
    try {
      const data = JSON.parse(e.dataTransfer.getData('text/plain'));
      if (data.type !== 'block') return;
      const block = blockedSlots.find(b => b.block_id === data.block_id);
      if (!block) return;
      const slotsPerHour = viewMode === 'day' ? 4 : 2;
      const hour = START_HOUR + Math.floor(slotIndex / slotsPerHour);
      const minutes = (slotIndex % slotsPerHour) * (60 / slotsPerHour);
      let newStartTime = setMinutes(setHours(new Date(day), hour), minutes);
      newStartTime.setSeconds(0);
      const durationMs = toUTCDate(block.end_time) - toUTCDate(block.start_time);
      await api.put(`/admin/blocked-slots/${block.block_id}`, { ...block, start_time: newStartTime.toISOString(), end_time: new Date(newStartTime.getTime() + durationMs).toISOString() });
      toast.success(language === 'pt' ? `Bloqueio movido para ${format(newStartTime, "dd/MM 'às' HH:mm")}` : `Block moved to ${format(newStartTime, "MM/dd 'at' HH:mm")}`);
      loadBlocks().catch(error => { console.error('Erro a atualizar bloqueios:', error); toast.error('Bloqueio movido, mas não foi possível atualizar a agenda.'); });
    } catch { toast.error(language === 'pt' ? 'Erro ao mover bloqueio' : 'Error moving block'); }
  }, [blockedSlots, viewMode, language, loadBlocks]);

  const clearDragHighlight = useCallback(() => {
    if (highlightedSlotRef.current) highlightedSlotRef.current.classList.remove('schedule-slot-drop-target');
    highlightedSlotRef.current = null;
  }, []);
  const handleDragStart = useCallback((e, booking) => {
    if (booking.status === 'cancelled' || booking.status === 'completed') { e.preventDefault(); return; }
    setDraggedBooking(booking); setIsDragging(true);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', booking.booking_id);
    e.target.style.opacity = '0.5';
  }, []);

  const handleDragEnd = useCallback((e) => {
    e.target.style.opacity = '1';
    clearDragHighlight();
    setDraggedBooking(null); setIsDragging(false);
  }, [clearDragHighlight]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault(); e.dataTransfer.dropEffect = 'move';
    if (highlightedSlotRef.current === e.currentTarget) return;
    clearDragHighlight();
    highlightedSlotRef.current = e.currentTarget;
    e.currentTarget.classList.add('schedule-slot-drop-target');
  }, [clearDragHighlight]);

  const handleDragLeave = useCallback((e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) clearDragHighlight();
  }, [clearDragHighlight]);

  const handleDrop = useCallback(async (e, day, slotIndex) => {
    e.preventDefault();
    if (!draggedBooking) return;
    const slotsPerHour = viewMode === 'day' ? 4 : 2;
    const hour = START_HOUR + Math.floor(slotIndex / slotsPerHour);
    const minutes = (slotIndex % slotsPerHour) * (60 / slotsPerHour);
    let newStartTime = setMinutes(setHours(new Date(day), hour), minutes);
    newStartTime.setSeconds(0); newStartTime.setMilliseconds(0);
    const oldStart = toUTCDate(draggedBooking.start_time);
    const oldLocal = getLisbonHourMinute(draggedBooking.start_time);
    if (isSameDayLisbon(draggedBooking.start_time, newStartTime) && oldLocal.hour === hour && oldLocal.minute === minutes) {
      clearDragHighlight(); setDraggedBooking(null); setIsDragging(false); return;
    }
    const dragService = getService(draggedBooking.service_id);
    const dragDuration = (dragService?.duration || 60) + (dragService?.buffer_time ?? 15);
    // REGRA 2: Admin/Staff podem sobrepor horários — verificação de conflito removida
    try {
      await api.put(`/admin/bookings/${draggedBooking.booking_id}/reschedule`, { start_time: newStartTime.toISOString() });
      const isoDrag = newStartTime.toISOString();
      const newEnd = new Date(newStartTime.getTime() + (dragService?.duration || 60) * 60000).toISOString();
      ++bookingRequestRef.current;
      setBookings(prev => prev.map(b => b.booking_id === draggedBooking.booking_id ? { ...b, start_time: isoDrag, end_time: newEnd } : b));
      toast.success(language === 'pt' ? `Reagendado para ${format(newStartTime, "dd/MM 'às' HH:mm", { locale })}` : `Rescheduled to ${format(newStartTime, "MM/dd 'at' HH:mm", { locale })}`);
    } catch (error) { toast.error(error.response?.data?.detail || 'Erro ao reagendar'); }
    clearDragHighlight(); setDraggedBooking(null); setIsDragging(false);
  }, [draggedBooking, viewMode, getService, language, locale, clearDragHighlight]);

  // Touch Drag reusable drop handler
  const touchDropAtSlot = useCallback(async (booking, day, slotIndex) => {
    const slotsPerHour = viewMode === 'day' ? 4 : 2;
    const hour = START_HOUR + Math.floor(slotIndex / slotsPerHour);
    const minutes = (slotIndex % slotsPerHour) * (60 / slotsPerHour);
    let newStartTime = setMinutes(setHours(new Date(day), hour), minutes);
    newStartTime.setSeconds(0); newStartTime.setMilliseconds(0);
    const oldStart = toUTCDate(booking.start_time);
    const oldLocal = getLisbonHourMinute(booking.start_time);
    if (isSameDayLisbon(booking.start_time, newStartTime) && oldLocal.hour === hour && oldLocal.minute === minutes) return;
    try {
      await api.put(`/admin/bookings/${booking.booking_id}/reschedule`, { start_time: newStartTime.toISOString() });
      const isoTouch = newStartTime.toISOString();
      const service = getService(booking.service_id);
      const newEnd = new Date(newStartTime.getTime() + (service?.duration || 60) * 60000).toISOString();
      ++bookingRequestRef.current;
      setBookings(prev => prev.map(b => b.booking_id === booking.booking_id ? { ...b, start_time: isoTouch, end_time: newEnd } : b));
      toast.success(language === 'pt' ? `Reagendado para ${format(newStartTime, "dd/MM 'às' HH:mm", { locale })}` : `Rescheduled to ${format(newStartTime, "MM/dd 'at' HH:mm", { locale })}`);
    } catch (error) { toast.error(error.response?.data?.detail || 'Erro ao reagendar'); }
  }, [viewMode, language, locale, getService]);

  const onTouchDragStart = useCallback(booking => { setDraggedBooking(booking); setIsDragging(true); }, []);
  const onTouchDragEnd = useCallback(() => { setDraggedBooking(null); setIsDragging(false); }, []);
  const { handleTouchStart } = useTouchDrag({ viewMode, onDragStart: onTouchDragStart, onDropAtSlot: touchDropAtSlot, onDragEnd: onTouchDragEnd });

  const getStatusColor = useCallback(status => {
    switch (status) {
      case 'confirmed': return { bg: 'bg-green-500', border: 'border-green-600', text: 'text-white' };
      case 'pending': return { bg: 'bg-amber-400', border: 'border-amber-500', text: 'text-amber-900' };
      case 'completed': return { bg: 'bg-blue-500', border: 'border-blue-600', text: 'text-white' };
      case 'cancelled': return { bg: 'bg-red-400', border: 'border-red-500', text: 'text-white' };
      default: return { bg: 'bg-gray-400', border: 'border-gray-500', text: 'text-white' };
    }
  }, []);

  const timeSlots = useMemo(() => {
    const slots = [];
    for (let hour = START_HOUR; hour < END_HOUR; hour++) {
      if (viewMode === 'day') { slots.push(`${String(hour).padStart(2,'0')}:00`); slots.push(`${String(hour).padStart(2,'0')}:15`); slots.push(`${String(hour).padStart(2,'0')}:30`); slots.push(`${String(hour).padStart(2,'0')}:45`); }
      else { slots.push(`${String(hour).padStart(2,'0')}:00`); slots.push(`${String(hour).padStart(2,'0')}:30`); }
    }
    return slots;
  }, [viewMode]);

  const handleDeleteCancelledBooking = async (bookingId) => {
    if (!window.confirm(language === 'pt' ? 'Excluir permanentemente esta marcação?' : 'Permanently delete this booking?')) return;
    try {
      await api.delete(`/admin/bookings/${bookingId}`);
      toast.success(language === 'pt' ? 'Marcação excluída!' : 'Booking deleted!');
      setDetailsDialogOpen(false);
      setBookings(prev => prev.filter(b => b.booking_id !== bookingId));
      loadCancelledCount();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erro ao excluir'); }
  };

  if (masterLoading || (scheduleLoading && !hasScheduleLoaded)) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] pt-24 flex items-center justify-center">
        <div className="animate-pulse text-[#BFA05F]">A carregar agenda...</div>
      </div>
    );
  }
  if (masterError || (scheduleError && !hasScheduleLoaded)) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] pt-24 flex flex-col items-center gap-4">
        <p className="text-red-600">Não foi possível carregar todas as informações da Agenda.</p>
        <Button onClick={() => { if (masterError) loadMaster(); if (scheduleError) refreshVisibleData().then(() => { setScheduleError(false); setHasScheduleLoaded(true); }).catch(() => toast.error('Não foi possível carregar a agenda.')); }}>Tentar novamente</Button>
      </div>
    );
  }

  return (
    <div className={`min-h-screen min-w-0 bg-[#F9F8F6] pt-20 pb-8 ${viewMode === 'week' ? 'schedule-week-active' : ''}`} data-testid="schedule-page">
      <style dangerouslySetInnerHTML={{ __html: touchDragStyles }} />
      <div className="max-w-[1600px] mx-auto px-3 sm:px-4 lg:px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 schedule-main-toolbar">
          <div className="flex items-center gap-3 min-w-0">
            {isAdmin && (
              <Button variant="outline" size="sm" aria-label={language === 'pt' ? 'Abrir filtros' : 'Open filters'} onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden rounded-full h-10 w-10 p-0 shrink-0">
                <Menu className="w-5 h-5" />
              </Button>
            )}
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-medium text-[#1A1815]">{language === 'pt' ? 'Agenda' : 'Schedule'}</h1>
              <p className="text-[#635E54] text-sm mt-1">{format(currentDate, "MMMM yyyy", { locale })}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <div className="flex items-center gap-1 bg-white rounded-full p-1 border border-[#E6E2DA]">
              <Button variant="ghost" size="sm" onClick={() => navigateDate('prev')} className="rounded-full h-8 w-8 p-0"><ChevronLeft className="w-4 h-4" /></Button>
              <Button variant="ghost" size="sm" onClick={goToToday} className="px-3 h-8 text-sm">{language === 'pt' ? 'Hoje' : 'Today'}</Button>
              <Button variant="ghost" size="sm" onClick={() => navigateDate('next')} className="rounded-full h-8 w-8 p-0"><ChevronRight className="w-4 h-4" /></Button>
            </div>
            <div className="flex bg-white rounded-full p-1 border border-[#E6E2DA]">
              {[
                { mode: 'day', icon: <CalendarDays className="w-4 h-4 md:mr-1" />, label: language === 'pt' ? 'Dia' : 'Day' },
                { mode: 'week', icon: <CalendarRange className="w-4 h-4 md:mr-1" />, label: language === 'pt' ? 'Semana' : 'Week' },
                { mode: 'month', icon: <Calendar className="w-4 h-4 md:mr-1" />, label: language === 'pt' ? 'Mês' : 'Month' },
              ].map(({ mode, icon, label }) => (
                <Button key={mode} variant={viewMode === mode ? 'default' : 'ghost'} size="sm"
                  aria-label={label} title={label} onClick={() => setViewMode(mode)}
                  className={`rounded-full h-8 px-2 md:px-3 ${viewMode === mode ? 'bg-[#1A1815] text-white' : ''}`}>
                  {icon}<span className="hidden md:inline">{label}</span>
                </Button>
              ))}
            </div>
          </div>
        </div>

        {isStaff && <section className="mb-3" data-testid="schedule-client-booking-search">
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => setClientBookingSearchOpen(open => !open)}
              aria-expanded={clientBookingSearchOpen} aria-controls="schedule-client-booking-search-panel"
              data-testid="schedule-client-booking-search-toggle"
              className={`inline-flex items-center justify-center gap-2 min-h-10 px-4 rounded-full border text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-[#BFA05F] ${clientBookingSearchOpen ? 'border-[#BFA05F] bg-[#FBF4E5] text-[#755C2D]' : 'border-[#E6E2DA] bg-white text-[#1A1815] hover:border-[#BFA05F]'}`}>
              {clientBookingSearchOpen ? <X className="h-4 w-4" /> : <Search className="h-4 w-4" />}
              {clientBookingSearchOpen
                ? (language === 'pt' ? 'Fechar pesquisa' : 'Close search')
                : (language === 'pt' ? 'Pesquisar marcação' : 'Find booking')}
            </button>
            {activeClientBookingFilter && <>
              <span className="inline-flex items-center gap-2 text-xs sm:text-sm rounded-full bg-[#FBF4E5] text-[#755C2D] px-3 py-2 border border-[#E8D7AF] min-w-0">
                <User className="h-4 w-4 shrink-0" />{language === 'pt' ? 'A mostrar apenas:' : 'Showing only:'} <strong className="break-words">{activeClientBookingFilter.name}</strong>
              </span>
              <button type="button" data-testid="clear-schedule-client-filter" onClick={() => setActiveClientBookingFilter(null)}
                className="min-h-10 inline-flex items-center gap-1 text-xs font-semibold text-[#635E54] underline underline-offset-2">
                <X className="h-4 w-4" />{language === 'pt' ? 'Mostrar todas as marcações' : 'Show all bookings'}
              </button>
            </>}
          </div>
          {clientBookingSearchOpen && <div id="schedule-client-booking-search-panel" className="mt-3 rounded-xl border border-[#E6E2DA] bg-white p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <div className="min-w-0 flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8B857A] pointer-events-none" />
              <input type="search" value={clientBookingQuery} onChange={e => setClientBookingQuery(e.target.value)}
                placeholder={language === 'pt' ? 'Pesquisar marcações por nome, telemóvel ou email' : 'Find bookings by name, phone or email'}
                aria-label={language === 'pt' ? 'Pesquisar marcações por cliente' : 'Search client bookings'}
                className="w-full min-h-11 pl-10 pr-3 rounded-lg border border-[#E6E2DA] text-base bg-[#FCFBF9] text-[#1A1815] focus:ring-2 focus:ring-[#BFA05F] outline-none" />
            </div>
            <label className="inline-flex items-center gap-2 text-xs sm:text-sm text-[#635E54] cursor-pointer shrink-0">
              <input type="checkbox" checked={clientBookingIncludePast} onChange={e => setClientBookingIncludePast(e.target.checked)}
                className="w-4 h-4 accent-[#BFA05F]" />
              {language === 'pt' ? 'Incluir marcações anteriores' : 'Include past bookings'}
            </label>
          </div>
          {clientBookingQuery.trim().length >= 2 && <div className="pt-3 space-y-2" aria-live="polite">
            {clientBookingSearchLoading && <p className="text-sm text-[#635E54]">{language === 'pt' ? 'A pesquisar…' : 'Searching…'}</p>}
            {clientBookingSearchError && <p role="alert" className="text-sm text-red-600">{clientBookingSearchError}</p>}
            {!clientBookingSearchLoading && !clientBookingSearchError && clientBookingResults.length === 0 &&
              <p className="text-sm text-[#635E54]">{language === 'pt' ? 'Nenhuma marcação encontrada.' : 'No bookings found.'}</p>}
            {!clientBookingSearchLoading && clientBookingResults.length > 0 && <>
              <p className="text-xs text-[#8B857A]">{language === 'pt' ? `${clientBookingResults.length} marcação(ões) encontrada(s) — toca para abrir o dia` : `${clientBookingResults.length} booking(s) — tap to open the day`}</p>
              <div className="max-h-72 overflow-y-auto space-y-1.5" data-testid="schedule-client-booking-results">
                {clientBookingResults.map(result => <button type="button" key={result.booking_id}
                  onClick={() => jumpToClientBooking(result)}
                  className="w-full text-left rounded-lg border border-[#E6E2DA] bg-[#FCFBF9] p-3 hover:border-[#BFA05F] focus:outline-none focus:ring-2 focus:ring-[#BFA05F]">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-semibold text-sm text-[#1A1815]">{result.client_name}</span>
                    <span className="text-sm font-semibold text-[#80632C]">{new Date(result.start_time).toLocaleDateString(language === 'pt' ? 'pt-PT' : 'en-GB', { timeZone: 'Europe/Lisbon', day: '2-digit', month: '2-digit', year: 'numeric' })} · {formatLisbon(result.start_time)}</span>
                  </div>
                  <p className="text-xs text-[#635E54] mt-1 break-words">{result.service_name} · {result.staff_name} · {result.status === 'confirmed' ? (language === 'pt' ? 'Confirmada' : 'Confirmed') : (language === 'pt' ? 'Pendente' : 'Pending')}</p>
                </button>)}
              </div>
            </>}
          </div>}
          </div>}
        </section>}

        <div className="flex min-w-0 gap-4 relative">
          {/* Sidebar */}
          {isAdmin && (
            <div className={`${sidebarOpen ? 'fixed' : 'hidden'} lg:relative lg:block w-[min(88vw,320px)] lg:w-56 flex-shrink-0 z-50 lg:z-auto inset-y-0 left-0 lg:inset-auto pt-20 lg:pt-0 px-3 lg:px-0`}>
              <div className={`bg-white rounded-2xl p-4 border border-[#E6E2DA] lg:sticky lg:top-24 h-full lg:h-auto max-h-[calc(100dvh-80px)] lg:max-h-[calc(100dvh-112px)] overflow-y-auto shadow-lg lg:shadow-none ${viewMode !== 'month' ? 'schedule-week-sidebar' : ''}`}>
                <div className="flex items-center justify-between gap-2 mb-3">
                  <h3 className="font-medium text-[#1A1815] text-sm">{language === 'pt' ? 'Colaboradores' : 'Staff'}</h3>
                  <button type="button" aria-label={language === 'pt' ? 'Fechar filtros' : 'Close filters'} onClick={() => setSidebarOpen(false)} className="lg:hidden p-2 rounded-full hover:bg-[#F9F8F6]"><X className="w-4 h-4" /></button>
                </div>
                <button onClick={() => setSelectedStaffId(null)}
                  className={`w-full flex items-center gap-2 p-2 rounded-lg mb-2 transition-colors text-sm ${!selectedStaffId ? 'bg-[#BFA05F]/10 border border-[#BFA05F]' : 'hover:bg-[#F2F0EB]'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${!selectedStaffId ? 'bg-[#BFA05F]' : 'bg-[#E6E2DA]'}`}>
                    <User className={`w-4 h-4 ${!selectedStaffId ? 'text-white' : 'text-[#635E54]'}`} />
                  </div>
                  <span className={!selectedStaffId ? 'font-medium text-[#BFA05F]' : 'text-[#635E54]'}>{language === 'pt' ? 'Todos' : 'All'}</span>
                </button>
                {staff.map(member => (
                  <button key={member.staff_id} onClick={() => setSelectedStaffId(member.staff_id)}
                    className={`w-full flex items-center gap-2 p-2 rounded-lg mb-1 transition-colors text-sm ${selectedStaffId === member.staff_id ? 'bg-[#BFA05F]/10 border border-[#BFA05F]' : 'hover:bg-[#F2F0EB]'}`}>
                    {member.photo && member.photo.startsWith('http')
                      ? <img src={member.photo} alt="" className="w-8 h-8 rounded-full object-cover" />
                      : <div className="w-8 h-8 rounded-full bg-[#BFA05F]/20 flex items-center justify-center"><User className="w-4 h-4 text-[#BFA05F]" /></div>}
                    <span className={`truncate ${selectedStaffId === member.staff_id ? 'font-medium' : ''}`}>{member.name.split(' ')[0]}</span>
                  </button>
                ))}
                <div className="border-t border-[#E6E2DA] my-3 pt-3">
                  <h3 className="font-medium text-[#1A1815] mb-2 text-sm">{language === 'pt' ? 'Filtros' : 'Filters'}</h3>
                  <button onClick={() => setShowCancelled(false)}
                    className={`w-full flex items-center gap-2 p-2 rounded-lg mb-1 transition-colors text-sm ${!showCancelled ? 'bg-green-50 border border-green-300' : 'hover:bg-[#F2F0EB]'}`}>
                    <Calendar className={`w-4 h-4 ${!showCancelled ? 'text-green-600' : 'text-[#635E54]'}`} />
                    <span className={!showCancelled ? 'text-green-700 font-medium' : 'text-[#635E54]'}>{language === 'pt' ? 'Ativos' : 'Active'}</span>
                  </button>
                  <button onClick={() => setShowCancelled(true)}
                    className={`w-full flex items-center gap-2 p-2 rounded-lg transition-colors text-sm ${showCancelled ? 'bg-red-50 border border-red-300' : 'hover:bg-[#F2F0EB]'}`}>
                    <X className={`w-4 h-4 ${showCancelled ? 'text-red-600' : 'text-[#635E54]'}`} />
                    <span className={showCancelled ? 'text-red-700 font-medium' : 'text-[#635E54]'}>
                      {language === 'pt' ? 'Cancelados' : 'Cancelled'}
                      {cancelledCount > 0 && <span className="ml-1 px-1.5 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">{cancelledCount}</span>}
                    </span>
                  </button>
                </div>
                <div className="border-t border-[#E6E2DA] my-3 pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-[#1A1815] text-sm">{language === 'pt' ? 'Bloqueios' : 'Blocks'}</h3>
                    <Button variant="ghost" size="sm" onClick={() => { setEditingBlock(null); setBlockInitialValues(null); setBlockDialogOpen(true); }} className="h-7 px-2 text-xs">
                      <Plus className="w-3 h-3 mr-1" />{language === 'pt' ? 'Novo' : 'New'}
                    </Button>
                  </div>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {blockedSlots.length === 0
                      ? <p className="text-xs text-[#A8A29A] text-center py-2">{language === 'pt' ? 'Nenhum bloqueio' : 'No blocks'}</p>
                      : blockedSlots.slice(0, 5).map(block => (
                        <div key={block.block_id} className="bg-red-50 border border-red-200 rounded-lg p-2 text-xs">
                          <div className="flex items-start justify-between gap-1">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-red-900 truncate">{block.reason}</p>
                              <p className="text-red-700 text-[10px]">{formatLisbon(block.start_time)} - {formatLisbon(block.end_time)}</p>
                            </div>
                            <button onClick={() => handleDeleteBlockedSlot(block.block_id)} className="text-red-600 hover:text-red-800 p-0.5"><Trash2 className="w-3 h-3" /></button>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Botão de Limpeza — dentro da sidebar */}
                <div className="border-t border-[#E6E2DA] my-3 pt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full text-xs text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 rounded-full"
                    data-testid="cleanup-old-data-btn"
                    onClick={async () => {
                      if (!window.confirm(language === 'pt'
                        ? 'Excluir permanentemente todos os agendamentos cancelados e bloqueios anteriores a hoje?'
                        : 'Permanently delete all cancelled bookings and blocks before today?')) return;
                      try {
                        const res = await api.delete('/admin/cleanup/old-data');
                        const d = res.data;
                        toast.success(language === 'pt'
                          ? `Limpeza concluída: ${d.cancelled_deleted} cancelados + ${d.blocks_deleted} bloqueios removidos`
                          : `Cleanup done: ${d.cancelled_deleted} cancelled + ${d.blocks_deleted} blocks removed`);
                        refreshVisibleData().catch(error => { console.error('Falhou a atualização após limpeza:', error); toast.error('Limpeza concluída, mas não foi possível atualizar a agenda.'); });
                        loadCancelledCount();
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Erro');
                      }
                    }}
                  >
                    <Trash2 className="w-3 h-3 mr-1.5" />
                    {language === 'pt' ? 'Limpar Dados Antigos' : 'Clean Old Data'}
                  </Button>
                </div>

              </div>
            </div>
          )}

          {/* Área Principal */}
          <div className={`flex-1 min-w-0 bg-white rounded-2xl border border-[#E6E2DA] overflow-hidden flex flex-col min-h-0 ${viewMode !== 'month' ? 'schedule-week-panel' : ''}`}>
            {viewMode === 'month' && <div className="sm:hidden px-3 py-2 text-xs text-center text-[#635E54] bg-[#F9F8F6] border-b border-[#E6E2DA]">{language === 'pt' ? 'Toque num dia para abrir a agenda diária' : 'Tap a day to open the daily schedule'}</div>}
            {scheduleLoading && <div className="p-2 text-xs text-center text-[#BFA05F] bg-[#F9F8F6]">{language === 'pt' ? 'A atualizar agenda...' : 'Updating schedule...'}</div>}
            {scheduleError && <div className="p-3 text-sm text-center text-red-600">{language === 'pt' ? 'Erro ao carregar este período.' : 'Error loading this period.'} <button className="underline" onClick={() => refreshVisibleData().then(() => setScheduleError(false)).catch(() => toast.error('Erro ao carregar agenda'))}>{language === 'pt' ? 'Tentar novamente' : 'Retry'}</button></div>}
            {(() => {
              const safeDate = (currentDate instanceof Date && !isNaN(currentDate.getTime())) ? currentDate : new Date();
              try {
                if (viewMode === 'month') {
                  const startMonth = startOfMonth(safeDate);
                  const startW = startOfWeek(startMonth, { weekStartsOn: 1 });
                  const endW = endOfWeek(endOfMonth(startMonth), { weekStartsOn: 1 });
                  const monthDaysList = eachDayOfInterval({ start: startW, end: endW });
                  return (
                    <div className="flex flex-col h-full">
                      <div className="py-6 border-b border-[#E6E2DA] bg-white flex justify-center items-center">
                        <h2 className="text-2xl font-bold text-[#1A1815] capitalize">{format(safeDate, 'MMMM yyyy', { locale })}</h2>
                      </div>
                      <div className="schedule-calendar-horizontal flex-1">
                        <div className="min-w-[700px] xl:min-w-0">
                          <div className="grid grid-cols-7 border-b border-[#E6E2DA] bg-[#F9F8F6]">
                            {(language === 'pt' ? ['seg','ter','qua','qui','sex','sáb','dom'] : ['mon','tue','wed','thu','fri','sat','sun']).map(d => (
                              <div key={d} className="p-2 text-center text-[10px] font-bold text-[#635E54] uppercase border-r border-[#E6E2DA] last:border-r-0">{d}</div>
                            ))}
                          </div>
                          <div className="grid grid-cols-7 flex-1 overflow-y-auto bg-white">
                            {monthDaysList.map((dayDate, i) => {
                              const dayBookings = getBookingsForDay(dayDate);
                              const isToday = isSameDay(dayDate, new Date());
                              const isCurrentMonth = isSameMonth(dayDate, safeDate);
                              return (
                                <div key={i} onClick={() => { setCurrentDate(new Date(dayDate.getTime())); setViewMode('day'); }}
                                  className={`min-h-[130px] p-1 border-r border-b border-[#E6E2DA] hover:bg-[#F9F8F6] transition-colors cursor-pointer group relative ${!isCurrentMonth ? 'bg-gray-50/30 opacity-40' : 'bg-white'}`}>
                                  <div className="flex justify-between items-start mb-1 p-1">
                                    <span className={`text-xs font-bold h-6 w-6 flex items-center justify-center rounded-full ${isToday ? 'bg-[#BFA05F] text-white' : 'text-[#1A1815]'}`}>{format(dayDate, 'd')}</span>
                                  </div>
                                  <div className="flex flex-col gap-1 px-0.5">
                                    {dayBookings.slice(0, 6).map(booking => {
                                      const timeDisplay = formatLisbon(booking.start_time);
                                      let statusColor = '#FBBF24';
                                      if (booking.status === 'completed') statusColor = '#3B82F6';
                                      else if (booking.status === 'confirmed' || booking.payment_status === 'paid') statusColor = '#10B981';
                                      else if (booking.status === 'cancelled') statusColor = '#EF4444';
                                      return (
                                        <div key={booking.booking_id}
                                          onDoubleClick={(e) => { e.stopPropagation(); handleBookingDoubleClick(booking); }}
                                          className="text-[10px] px-2 py-1 rounded shadow-sm truncate flex items-center gap-1 border border-black/5"
                                          style={{ backgroundColor: statusColor, color: '#FFFFFF', fontWeight: '700', minHeight: '22px' }}>
                                          <span className="font-bold opacity-80 text-[9px] shrink-0 border-r border-white/20 pr-1">{timeDisplay}</span>
                                          <span className="truncate">{booking.user?.name || booking.guest_name || 'Cliente'}</span>
                                        </div>
                                      );
                                    })}
                                    {dayBookings.length > 6 && <div className="text-[9px] text-[#A8A29A] text-center font-bold mt-0.5">+ {dayBookings.length - 6}</div>}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <>
                    {viewMode === 'week' && (
                      <MobileFocusWeek weekDays={weekDays} currentDate={currentDate} language={language} locale={locale}
                        isStaff={isStaff} selectedStaffId={selectedStaffId} getBookingsForDay={getBookingsForDay} getBlockedSlotsForDay={getBlockedSlotsForDay}
                        getService={getService} getStatusColor={getStatusColor} formatTime={formatTime}
                        onSelectDay={day => setCurrentDate(new Date(day.getTime()))}
                        onOpenBooking={handleBookingDoubleClick} onOpenBlock={handleBlockDoubleClick}
                        onEmptySlot={handleSlotDoubleClick} onBookingAction={handleBookingAction} />
                    )}
                    <div ref={weekLayoutRef} className="schedule-week-layout flex-1" data-mobile-week-hidden={viewMode === 'week' ? 'true' : undefined} data-testid="schedule-axis-layout">
                    <div className="schedule-week-corner" aria-hidden="true" />
                    <div ref={weekHeaderRef} className="schedule-week-header-viewport" onScroll={syncWeekHorizontal}>
                      <div className="schedule-week-header-content" data-view={viewMode}>
                        {viewMode === 'day' ? (
                          <div className="schedule-week-header-cell p-2 text-center bg-[#F9F8F6]">
                            <div className="text-xs text-[#635E54] uppercase">{format(safeDate, 'EEEE', { locale })}</div>
                            <div className={`text-2xl font-bold ${isSameDay(safeDate, new Date()) ? 'text-[#BFA05F]' : 'text-[#1A1815]'}`}>{format(safeDate, 'd')}</div>
                          </div>
                        ) : weekDays.map((day, index) => (
                          <div key={index} data-schedule-day-index={index} className={`schedule-week-header-cell p-2 text-center border-r border-[#E6E2DA] last:border-r-0 ${isSameDay(day, new Date()) ? 'bg-[#BFA05F]/10' : 'bg-[#F9F8F6]'}`}>
                            <div className="text-xs text-[#635E54] uppercase">{format(day, 'EEE', { locale })}</div>
                            <div className={`text-lg font-semibold ${isSameDay(day, new Date()) ? 'text-[#BFA05F]' : 'text-[#1A1815]'}`}>{format(day, 'd')}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div ref={weekTimeRef} className="schedule-week-time-viewport" onScroll={syncWeekVertical}>
                      {timeSlots.map((time, index) => (
                        <div key={time} className="h-[60px] border-b border-[#E6E2DA] px-2 flex items-start pt-1">
                          {index % 2 === 0 && <span className="text-xs text-[#A8A29A]">{time}</span>}
                        </div>
                      ))}
                    </div>
                    <div ref={weekBoardRef} className="schedule-week-board-viewport" onScroll={syncWeekDesktopScroll}>
                      <div className="schedule-week-board-content" data-view={viewMode}>
                        {viewMode === 'day' ? (
                          <DayColumn day={safeDate} bookings={getBookingsForDay(safeDate)} blockedSlots={getBlockedSlotsForDay(safeDate)}
                            getBlockStyle={getBlockStyle} getService={getService} getStatusColor={getStatusColor}
                            formatTime={formatTime} getEndTime={getEndTime} handleBookingAction={handleBookingAction}
                            isStaff={isStaff} timeSlots={timeSlots} language={language} viewMode={viewMode}
                            handleSlotDoubleClick={handleSlotDoubleClick} handleBookingDoubleClick={handleBookingDoubleClick}
                            handleBlockDoubleClick={handleBlockDoubleClick} handleBlockDragStart={handleBlockDragStart}
                            handleBlockDrop={handleBlockDrop} selectedStaffId={selectedStaffId}
                            handleDragStart={handleDragStart} handleDragEnd={handleDragEnd}
                            handleDragOver={handleDragOver} handleDragLeave={handleDragLeave}
                            handleDrop={handleDrop} isDragging={isDragging}
                            handleTouchStart={handleTouchStart} />
                        ) : weekDays.map((day, dayIndex) => (
                          <DayColumn key={dayIndex} day={day} bookings={getBookingsForDay(day)} blockedSlots={getBlockedSlotsForDay(day)}
                            getBlockStyle={getBlockStyle} getService={getService} getStatusColor={getStatusColor}
                            formatTime={formatTime} getEndTime={getEndTime} handleBookingAction={handleBookingAction}
                            isStaff={isStaff} timeSlots={timeSlots} language={language} viewMode={viewMode}
                            isToday={isSameDay(day, new Date())} handleSlotDoubleClick={handleSlotDoubleClick}
                            handleBookingDoubleClick={handleBookingDoubleClick} handleBlockDoubleClick={handleBlockDoubleClick}
                            handleBlockDragStart={handleBlockDragStart} handleBlockDrop={handleBlockDrop}
                            selectedStaffId={selectedStaffId} handleDragStart={handleDragStart}
                            handleDragEnd={handleDragEnd} handleDragOver={handleDragOver}
                            handleDragLeave={handleDragLeave} handleDrop={handleDrop}
                            isDragging={isDragging} handleTouchStart={handleTouchStart} />
                        ))}
                      </div>
                    </div>
                  </div>
                  </>
                );
              } catch (e) { return <div className="p-10 text-center">Erro.</div>; }
            })()}
          </div>
        </div>

        {/* Overlay mobile */}
        {isAdmin && sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

        {/* Dialog: Ação no slot */}
        <Dialog open={slotActionMenuOpen} onOpenChange={setSlotActionMenuOpen}>
          <DialogContent className="schedule-dialog-content schedule-dialog-sheet sm:max-w-[360px]">
            <DialogHeader className="schedule-dialog-header">
              <DialogTitle className="text-[#1A1815] text-center text-lg sm:text-xl">{language === 'pt' ? 'O que deseja fazer?' : 'What would you like to do?'}</DialogTitle>
            </DialogHeader>
            <div className="schedule-dialog-scroll space-y-3">
              {selectedSlotInfo && (
                <p className="text-center text-sm text-[#635E54] mb-4">
                  {format(selectedSlotInfo.dateTime, "dd/MM/yyyy 'às' HH:mm", { locale: language === 'pt' ? pt : enGB })}
                </p>
              )}
              <button onClick={() => handleSlotActionSelect('existing-client')}
                className="w-full min-w-0 flex items-center gap-3 p-3 sm:p-4 rounded-lg border-2 border-[#BFA05F] bg-[#BFA05F]/5 hover:bg-[#BFA05F]/10 transition-colors">
                <User className="w-5 h-5 text-[#BFA05F]" />
                <div className="text-left">
                  <p className="font-semibold text-[#1A1815]">{language === 'pt' ? 'Cliente Existente' : 'Existing Client'}</p>
                  <p className="text-xs text-[#635E54]">{language === 'pt' ? 'Selecionar da lista' : 'Select from list'}</p>
                </div>
              </button>
              <button onClick={() => handleSlotActionSelect('new-client')}
                className="w-full min-w-0 flex items-center gap-3 p-3 sm:p-4 rounded-lg border-2 border-[#635E54] hover:border-[#BFA05F] hover:bg-[#BFA05F]/5 transition-colors">
                <UserPlus className="w-5 h-5 text-[#635E54]" />
                <div className="text-left">
                  <p className="font-semibold text-[#1A1815]">{language === 'pt' ? 'Novo Cliente' : 'New Client'}</p>
                  <p className="text-xs text-[#635E54]">{language === 'pt' ? 'Preencher dados manualmente' : 'Fill in details manually'}</p>
                </div>
              </button>
              <button onClick={() => handleSlotActionSelect('block')}
                className="w-full min-w-0 flex items-center gap-3 p-3 sm:p-4 rounded-lg border-2 border-red-300 hover:border-red-500 hover:bg-red-50 transition-colors">
                <Ban className="w-5 h-5 text-red-500" />
                <div className="text-left">
                  <p className="font-semibold text-[#1A1815]">{language === 'pt' ? 'Bloquear Horário' : 'Block Time Slot'}</p>
                  <p className="text-xs text-[#635E54]">{language === 'pt' ? 'Ex: Almoço, Reunião...' : 'E.g: Lunch, Meeting...'}</p>
                </div>
              </button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Criar Marcação — componente isolado */}
        <BookingForm
          open={bookingDialogOpen}
          onClose={() => { setBookingDialogOpen(false); setNextBookingSource(null); }}
          onSubmit={handleCreateBookingSubmit}
          staff={staff} services={sortedServices} clients={clients} clientsLoaded={clientsLoaded} clientsLoading={clientsLoading}
          initialStartTime={bookingInitialTime} language={language}
          initialExisting={bookingStartAsExisting} bookingSaving={bookingSaving}
          sourceBooking={nextBookingSource}
        />

        {/* Detalhes / Editar Marcação */}
        <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
          <DialogContent className="schedule-dialog-content schedule-dialog-fullscreen sm:max-w-[520px]">
            <DialogHeader className="schedule-dialog-header">
              <DialogTitle className="text-[#1A1815] text-lg sm:text-xl leading-snug">{language === 'pt' ? 'Detalhes da Marcação' : 'Booking Details'}</DialogTitle>
            </DialogHeader>
            {selectedBooking && (
              <div className="schedule-dialog-scroll space-y-4">
                {!editMode ? (
                  <>
                    <div className="bg-[#F9F8F6] p-4 rounded-xl space-y-3">
                      <div>
                        <label className="text-xs text-[#A8A29A] uppercase">{language === 'pt' ? 'Cliente' : 'Client'}</label>
                        <p className="text-[#1A1815] font-medium">{selectedBooking.user?.name || selectedBooking.guest_name || 'Cliente'}</p>
                      </div>
                      <div>
                        <label className="text-xs text-[#A8A29A] uppercase">{language === 'pt' ? 'Serviço' : 'Service'}</label>
                        <p className="text-[#1A1815] font-medium">{language === 'pt' ? getService(selectedBooking.service_id)?.name_pt : getService(selectedBooking.service_id)?.name_en}</p>
                        <p className="text-sm text-[#635E54]">{euro(selectedBooking.price ?? getService(selectedBooking.service_id)?.price)} • {getService(selectedBooking.service_id)?.duration}min</p>
                        {selectedBooking.adjustment_type && selectedBooking.adjustment_type !== 'none' && <div className="text-xs mt-1 text-[#635E54]">
                          {selectedBooking.adjustment_type === 'discount' ? (language === 'pt' ? 'Desconto' : 'Discount') : (language === 'pt' ? 'Acréscimo' : 'Surcharge')}: {selectedBooking.adjustment_percent}%
                          <span className="ml-2">({euro(selectedBooking.base_price ?? getService(selectedBooking.service_id)?.price)} → {euro(selectedBooking.price)})</span>
                        </div>}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 schedule-modal-field-grid">
                        <div>
                          <label className="text-xs text-[#A8A29A] uppercase">{language === 'pt' ? 'Início' : 'Start'}</label>
                          <p className="text-[#1A1815] font-medium">{formatLisbon(selectedBooking.start_time)}</p>
                          <p className="text-xs text-[#635E54]">{toUTCDate(selectedBooking.start_time)?.toLocaleDateString('pt-PT', { timeZone: 'Europe/Lisbon' })}</p>
                        </div>
                        <div>
                          <label className="text-xs text-[#A8A29A] uppercase">{language === 'pt' ? 'Fim' : 'End'}</label>
                          <p className="text-[#1A1815] font-medium">{formatLisbon(selectedBooking.end_time)}</p>
                          <p className="text-xs text-[#635E54]">{toUTCDate(selectedBooking.end_time)?.toLocaleDateString('pt-PT', { timeZone: 'Europe/Lisbon' })}</p>
                        </div>
                      </div>
                    </div>
                    {isStaff && selectedBooking.status !== 'cancelled' && (
                      <section className="pt-3 border-t border-[#E6E2DA] space-y-3" data-testid="schedule-booking-actions">
                        <p className="text-xs font-bold tracking-wide uppercase text-[#817A70]">{language === 'pt' ? 'Ações da marcação' : 'Booking actions'}</p>
                        {selectedBooking.status === 'pending' && (
                          <Button type="button" data-testid="schedule-confirm-booking" disabled={confirmStatusBusy}
                            onClick={async () => {
                              if (confirmStatusBusy) return;
                              setConfirmStatusBusy(true);
                              try {
                                await api.put(`/admin/bookings/${selectedBooking.booking_id}/status`, { status: 'confirmed' });
                                setBookings(prev => prev.map(b => b.booking_id === selectedBooking.booking_id ? { ...b, status: 'confirmed' } : b));
                                setSelectedBooking(prev => prev?.booking_id === selectedBooking.booking_id ? { ...prev, status: 'confirmed' } : prev);
                                toast.success(language === 'pt' ? 'Marcação confirmada! O pagamento continua pendente.' : 'Booking confirmed! Payment remains pending.');
                              } catch (err) {
                                toast.error(err?.response?.data?.detail || (language === 'pt' ? 'Não foi possível confirmar a marcação.' : 'Could not confirm booking.'));
                              } finally { setConfirmStatusBusy(false); }
                            }}
                            className="w-full min-h-12 rounded-xl bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-50">
                            <Check className="w-4 h-4 mr-2" />{confirmStatusBusy ? (language === 'pt' ? 'A confirmar…' : 'Confirming…') : (language === 'pt' ? 'Confirmar marcação' : 'Confirm booking')}
                          </Button>
                        )}
                        {selectedBooking.status === 'completed' ? (
                          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-2" data-testid="schedule-reopen-completed">
                            <p className="text-xs text-amber-900">{language === 'pt' ? 'Concluída por engano? Anula a conclusão, o pagamento manual e os pontos atribuídos.' : 'Completed by mistake? Reverse completion, manual payment and awarded points.'}</p>
                            <Button type="button" onClick={reopenCompletedBooking} disabled={undoCompletionBusy}
                              className="w-full min-h-11 rounded-xl bg-amber-700 text-white hover:bg-amber-800 disabled:opacity-60">
                              <RefreshCw className="w-4 h-4 mr-2" />{undoCompletionBusy ? (language === 'pt' ? 'A reverter…' : 'Reopening…') : (language === 'pt' ? 'Voltar a confirmado' : 'Return to confirmed')}
                            </Button>
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 gap-2">
                            <Button type="button" onClick={() => setEditMode(true)}
                              className="min-h-11 rounded-xl bg-[#BFA05F] text-white hover:bg-[#9E8246] text-sm px-2">
                              <Edit className="w-4 h-4 mr-1.5 shrink-0" />{language === 'pt' ? 'Editar' : 'Edit'}
                            </Button>
                            <Button type="button" onClick={async () => {
                              if (window.confirm(language === 'pt' ? 'Concluir marcação e confirmar pagamento?' : 'Complete booking and confirm payment?')) {
                                try {
                                  if (selectedBooking.payment_status !== 'paid') {
                                    try { await api.put(`/admin/bookings/${selectedBooking.booking_id}/confirm-payment`); }
                                    catch (payErr) { console.warn('Pagamento já processado:', payErr); }
                                  }
                                  await new Promise(resolve => setTimeout(resolve, 100));
                                  await api.put(`/admin/bookings/${selectedBooking.booking_id}/status`, { status: 'completed' });
                                  setBookings(prev => prev.map(b => b.booking_id === selectedBooking.booking_id ? { ...b, status: 'completed' } : b));
                                  toast.success(language === 'pt' ? 'Serviço concluído!' : 'Service completed!');
                                  setDetailsDialogOpen(false);
                                  loadBookings().catch(error => console.error('Erro a atualizar agenda:', error));
                                } catch (err) {
                                  loadBookings().catch(error => console.error('Erro a atualizar agenda:', error));
                                  toast.error(language === 'pt' ? 'Erro ao mudar estado; confirma o pagamento antes de repetir.' : 'Error changing status; check payment before retrying.');
                                }
                              }
                            }} className="min-h-11 rounded-xl bg-blue-700 text-white hover:bg-blue-800 text-sm px-2">
                              <Check className="w-4 h-4 mr-1.5 shrink-0" />{language === 'pt' ? 'Concluir' : 'Complete'}
                            </Button>
                            <Button type="button" variant="outline" onClick={async () => {
                              if (window.confirm(language === 'pt' ? 'Cancelar esta marcação?' : 'Cancel this booking?')) {
                                try {
                                  await api.put(`/admin/bookings/${selectedBooking.booking_id}/status`, { status: 'cancelled' });
                                  setBookings(prev => prev.map(b => b.booking_id === selectedBooking.booking_id ? { ...b, status: 'cancelled' } : b));
                                  toast.success(language === 'pt' ? 'Marcação cancelada.' : 'Booking cancelled.');
                                  setDetailsDialogOpen(false); loadCancelledCount();
                                } catch { toast.error(language === 'pt' ? 'Erro ao cancelar.' : 'Could not cancel.'); }
                              }
                            }} className="min-h-11 rounded-xl text-red-700 border-red-200 hover:bg-red-50 text-sm px-2">
                              <X className="w-4 h-4 mr-1.5 shrink-0" />{language === 'pt' ? 'Cancelar' : 'Cancel'}
                            </Button>
                            <Button type="button" data-testid="schedule-book-next" onClick={startNextBooking}
                              className="min-h-11 rounded-xl bg-[#1A1815] text-white hover:bg-[#333] text-sm px-2">
                              <CalendarDays className="w-4 h-4 mr-1.5 shrink-0" />{language === 'pt' ? 'Marcar próxima' : 'Book next'}
                            </Button>
                          </div>
                        )}
                        {selectedBooking.status === 'completed' && (
                          <Button type="button" data-testid="schedule-book-next" onClick={startNextBooking}
                            className="w-full min-h-11 rounded-xl bg-[#1A1815] text-white hover:bg-[#333]">
                            <CalendarDays className="w-4 h-4 mr-2" />{language === 'pt' ? 'Marcar próxima' : 'Book next'}
                          </Button>
                        )}
                      </section>
                    )}
                    {isStaff && selectedBooking.status === 'cancelled' && (
                      <div className="pt-4 border-t border-[#E6E2DA]">
                        <Button onClick={() => handleDeleteCancelledBooking(selectedBooking.booking_id)} className="w-full bg-red-600 text-white hover:bg-red-700 rounded-full">
                          <Trash2 className="w-4 h-4 mr-2" />{language === 'pt' ? 'Excluir Permanentemente' : 'Delete Permanently'}
                        </Button>
                        <p className="text-xs text-[#635E54] text-center mt-2">{language === 'pt' ? 'Esta ação não pode ser desfeita' : 'This action cannot be undone'}</p>
                      </div>
                    )}
                  </>
                ) : (
                  clientsLoaded ? <EditBookingForm
                    booking={selectedBooking} services={sortedServices} staff={staff} clients={clients}
                    language={language} onSave={handleEditBookingSave} onBack={() => setEditMode(false)}
                  /> : <div className="p-4 text-sm text-center text-[#635E54]">{clientsLoading ? 'A carregar clientes...' : <button className="underline" onClick={() => loadClients(true)}>Não foi possível carregar os clientes. Tentar novamente</button>}</div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Bloqueios — componente isolado */}
        <BlockForm
          open={blockDialogOpen}
          onClose={() => { setBlockDialogOpen(false); setEditingBlock(null); }}
          editingBlock={editingBlock} staff={staff} initialValues={blockInitialValues}
          language={language} onSave={handleSaveBlock} onDelete={handleDeleteBlockedSlot}
        />

        {/* Legenda */}
        <div className="mt-4 flex flex-wrap gap-4 justify-center">
          {[
            { color: 'bg-amber-400', label: language === 'pt' ? 'Pendente' : 'Pending' },
            { color: 'bg-green-500', label: language === 'pt' ? 'Confirmado' : 'Confirmed' },
            { color: 'bg-blue-500', label: language === 'pt' ? 'Concluído' : 'Completed' },
            { color: 'bg-red-500', label: language === 'pt' ? 'Bloqueado' : 'Blocked' },
          ].map(({ color, label }) => (
            <div key={label} className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded ${color}`} />
              <span className="text-xs text-[#635E54]">{label}</span>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full border-2 border-dashed border-gray-300" />
            <span className="text-xs text-[#635E54]">{language === 'pt' ? 'Buffer/Intervalo' : 'Buffer Time'}</span>
          </div>
          <div className="flex items-center gap-2">
            <Move className="w-3 h-3 text-[#BFA05F]" />
            <span className="text-xs text-[#635E54]">{language === 'pt' ? 'Arrastar para reagendar' : 'Drag to reschedule'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// DayColumn — memo para evitar re-renders desnecessários
// ============================================================
const DayColumn = memo(({
  day, bookings, blockedSlots = [], getBlockStyle, getService,
  getStatusColor, formatTime, getEndTime, handleBookingAction,
  isStaff, timeSlots, language, viewMode, isToday,
  handleDragStart, handleDragEnd, handleDragOver, handleDragLeave,
  handleDrop, isDragging, handleSlotDoubleClick,
  handleBookingDoubleClick, handleBlockDoubleClick,
  handleBlockDragStart, handleBlockDrop, selectedStaffId,
  handleTouchStart
}) => {
  const getBlockSlotStyle = (block) => {
    const startDate = toUTCDate(block.start_time);
    const endDate = toUTCDate(block.end_time);
    if (!startDate || !endDate) return { top: 0, height: 0 };
    const gridStart = new Date(day);
    gridStart.setHours(START_HOUR, 0, 0, 0);
    const gridEnd = addDays(new Date(day.getFullYear(), day.getMonth(), day.getDate()), 1);
    const clippedStart = new Date(Math.max(gridStart.getTime(), startDate.getTime()));
    const clippedEnd = new Date(Math.min(gridEnd.getTime(), endDate.getTime()));
    if (clippedEnd <= clippedStart) return { top: 0, height: 0 };
    const { hour, minute } = getLisbonHourMinute(clippedStart);
    const minutesPerSlot = viewMode === 'day' ? 15 : 30;
    return {
      top: ((hour - START_HOUR) * 60 + minute) / minutesPerSlot * SLOT_HEIGHT,
      height: (clippedEnd.getTime() - clippedStart.getTime()) / 60000 / minutesPerSlot * SLOT_HEIGHT
    };
  };

  return (
    <div className={`relative border-r border-[#E6E2DA] last:border-r-0 ${isToday ? 'bg-[#BFA05F]/5' : ''} schedule-slot-area`} onDragLeave={handleDragLeave}>
      {timeSlots.map((time, index) => (
        <div key={index}
          data-slot-index={index} data-slot-day={day instanceof Date ? day.toISOString() : day}
          className={`h-[60px] border-b border-[#E6E2DA] transition-colors ${isDragging ? 'hover:bg-[#BFA05F]/10' : ''} ${isStaff && !isDragging ? 'cursor-pointer hover:bg-[#BFA05F]/5 group' : ''}`}
          onDragOver={handleDragOver}
          onDrop={(e) => { const data = e.dataTransfer.getData('text/plain'); if (data.startsWith('{')) handleBlockDrop(e, day, index); else handleDrop(e, day, index); }}
          onDoubleClick={() => handleSlotDoubleClick && handleSlotDoubleClick(day, index)}>
          {isStaff && !isDragging && (
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-30 transition-opacity pointer-events-none">
              <Plus className="w-6 h-6 text-[#BFA05F]" />
            </div>
          )}
        </div>
      ))}

      {isToday && <CurrentTimeLine viewMode={viewMode} />}

      {bookings.map(booking => {
        const { top, height, serviceHeight, duration, bufferTime } = getBlockStyle(booking);
        const service = getService(booking.service_id);
        const colors = getStatusColor(booking.status);
        const canDrag = booking.status !== 'cancelled' && booking.status !== 'completed' && isStaff;
        return (
          <div key={booking.booking_id}
            className={`absolute left-1 right-1 group ${canDrag ? 'cursor-grab active:cursor-grabbing schedule-card-draggable' : ''}`}
            style={{ top: `${top}px`, height: `${height}px`, zIndex: 10 }}
            draggable={canDrag} onDragStart={(e) => handleDragStart(e, booking)} onDragEnd={handleDragEnd}
            onTouchStart={canDrag ? (e) => handleTouchStart(e, booking, e.currentTarget) : undefined}>
            <div className={`${colors.bg} ${colors.border} ${colors.text} border-l-4 rounded-r-lg shadow-sm overflow-hidden hover:shadow-md transition-all ${canDrag ? 'hover:ring-2 hover:ring-[#BFA05F]/50' : ''} cursor-pointer`}
              style={{ height: `${serviceHeight}px` }}
              onDoubleClick={() => handleBookingDoubleClick && handleBookingDoubleClick(booking)}>
              <div className="p-1.5 h-full flex flex-col">
                <div className="flex items-start justify-between">
                  <span className="text-[10px] md:text-xs font-bold truncate flex-1">{formatTime(booking.start_time)} - {getEndTime(booking)}</span>
                  {canDrag && <Move className="w-3 h-3 opacity-0 group-hover:opacity-60 transition-opacity flex-shrink-0" />}
                </div>
                <div className="text-xs md:text-sm font-medium truncate">{language === 'pt' ? service?.name_pt : service?.name_en}</div>
                <div className="text-[10px] md:text-xs opacity-90 truncate">{booking.user?.name || 'Cliente'}</div>
                {duration >= 60 && <div className="text-[9px] md:text-[10px] opacity-70 mt-auto">{euro(booking.price ?? service?.price)}</div>}
                {booking.status === 'pending' && isStaff && (
                  <div className="flex gap-1 mt-auto opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={(e) => { e.stopPropagation(); handleBookingAction(booking.booking_id, 'confirmed'); }} className="p-0.5 bg-white/30 rounded hover:bg-white/50"><Check className="w-3 h-3" /></button>
                    <button onClick={(e) => { e.stopPropagation(); handleBookingAction(booking.booking_id, 'cancelled'); }} className="p-0.5 bg-white/30 rounded hover:bg-white/50"><X className="w-3 h-3" /></button>
                  </div>
                )}
              </div>
            </div>
            {bufferTime > 0 && (
              <div className="bg-gray-100 border-l-4 border-gray-300 border-dashed rounded-r-lg mt-0.5" style={{ height: `${(bufferTime / (viewMode === 'day' ? 15 : 30)) * SLOT_HEIGHT - 4}px` }}>
                <div className="p-1 text-[9px] text-gray-400 italic">{language === 'pt' ? 'Intervalo' : 'Buffer'} ({bufferTime}min)</div>
              </div>
            )}
          </div>
        );
      })}

      {blockedSlots
        .filter(block => !selectedStaffId || !block.staff_id || block.staff_id === selectedStaffId)
        .map(block => {
          const { top, height } = getBlockSlotStyle(block);
          return (
            <div key={block.block_id}
              className={`absolute left-1 right-1 group/block ${isStaff ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'}`}
              style={{ top: `${top}px`, height: `${height}px`, zIndex: 5 }}
              draggable={isStaff}
              onDragStart={(e) => handleBlockDragStart && handleBlockDragStart(e, block)}
              onDragEnd={(e) => { e.target.style.opacity = '1'; }}
              onDoubleClick={() => handleBlockDoubleClick && handleBlockDoubleClick(block)}
              title={language === 'pt' ? 'Duplo clique para editar • Arrastar para mover' : 'Double click to edit • Drag to move'}>
              <div className="bg-red-500/80 border-l-4 border-red-700 rounded-r-lg shadow-sm h-full flex items-center justify-center group-hover/block:bg-red-600/90 transition-colors group-hover/block:ring-2 group-hover/block:ring-red-400/50">
                <div className="text-white text-center px-2">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <Ban className="w-4 h-4" />
                    {isStaff && <Move className="w-3 h-3 opacity-0 group-hover/block:opacity-60" />}
                  </div>
                  <p className="text-[10px] font-bold">{block.reason}</p>
                  <p className="text-[9px] opacity-90">{formatLisbon(block.start_time)} - {formatLisbon(block.end_time)}</p>
                  {isStaff && <p className="text-[8px] opacity-70 mt-1 group-hover/block:opacity-100">{language === 'pt' ? '2x clique p/ editar' : '2x click to edit'}</p>}
                </div>
              </div>
            </div>
          );
        })}
    </div>
  );
});

// ============================================================
// CurrentTimeLine
// ============================================================
const CurrentTimeLine = ({ viewMode }) => {
  const [position, setPosition] = useState(-1);
  useEffect(() => {
    const updatePosition = () => {
      const now = new Date();
      const timeStr = now.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Lisbon' });
      const [hour, minute] = timeStr.split(':').map(Number);
      if (hour < START_HOUR || hour >= END_HOUR) { setPosition(-1); return; }
      setPosition(((hour - START_HOUR) * 60 + minute) / (viewMode === 'day' ? 15 : 30) * SLOT_HEIGHT);
    };
    updatePosition();
    const interval = setInterval(updatePosition, 60000);
    return () => clearInterval(interval);
  }, [viewMode]);
  if (position < 0) return null;
  return (
    <div className="absolute left-0 right-0 z-10 pointer-events-none" style={{ top: `${position}px` }}>
      <div className="flex items-center">
        <div className="w-2 h-2 bg-red-500 rounded-full" />
        <div className="flex-1 h-0.5 bg-red-500" />
      </div>
    </div>
  );
};

export default SchedulePage;